// D:\directiveai-react\agthealth\agthealth-frontend\src\services\agthealthService.js
import axios from 'axios';

// Configuración base para las peticiones al API según el entorno
const API_URL = process.env.NODE_ENV === 'development'
  ? 'http://localhost:5002/api'
  : (process.env.REACT_APP_API_HEALTH_URL);

console.log("API_URL configurada:", API_URL);

// Sistema de caché mejorado con tiempos de expiración y control de frecuencia
const apiCache = {
  // Datos en caché
  userProfile: null,
  patients: null,
  
  // Tiempos de última actualización
  lastFetch: {
    userProfile: null,
    patients: null,
    patientDetails: {},
    appointments: {},
    records: {},
    notes: {},
    tasks: {}
  },
  
  // Control de intentos de solicitud
  requestAttempts: {},
  
  // Promesas pendientes para consolidar solicitudes simultáneas
  pendingPromises: {},
  
  // Contadores de errores para backoff exponencial
  errorCount: {}
};

// Tiempos de expiración de caché (en milisegundos)
const CACHE_EXPIRY = {
  userProfile: 5 * 60 * 1000, // 5 minutos
  patients: 3 * 60 * 1000, // 3 minutos
  patientDetails: 5 * 60 * 1000, // 5 minutos
  appointments: 2 * 60 * 1000, // 2 minutos
  records: 5 * 60 * 1000, // 5 minutos
  notes: 3 * 60 * 1000, // 3 minutos
  tasks: 3 * 60 * 1000 // 3 minutos
};

// Tiempos mínimos entre solicitudes (throttling, en milisegundos)
const MIN_REQUEST_INTERVAL = {
  userProfile: 3000, // 3 segundos
  patients: 2000, // 2 segundos
  patientDetails: 3000, // 3 segundos
  appointments: 2000, // 2 segundos
  records: 3000, // 3 segundos
  notes: 2000, // 2 segundos
  tasks: 2000 // 2 segundos
};

// Función para verificar si la caché está vigente
const isCacheValid = (cacheType, specificKey = null) => {
  const key = specificKey ? `${cacheType}_${specificKey}` : cacheType;
  const lastFetch = specificKey ? apiCache.lastFetch[cacheType]?.[specificKey] : apiCache.lastFetch[cacheType];
  const expiryTime = CACHE_EXPIRY[cacheType];
  
  return lastFetch && (Date.now() - lastFetch < expiryTime);
};

// Función para verificar si se puede realizar una nueva solicitud (throttling)
const canMakeRequest = (cacheType, specificKey = null) => {
  const key = specificKey ? `${cacheType}_${specificKey}` : cacheType;
  const lastFetch = specificKey ? apiCache.lastFetch[cacheType]?.[specificKey] : apiCache.lastFetch[cacheType];
  const minInterval = MIN_REQUEST_INTERVAL[cacheType];
  
  return !lastFetch || (Date.now() - lastFetch > minInterval);
};

// Función para calcular tiempo de espera con backoff exponencial
const calculateBackoffTime = (errorCount, baseTime = 1000, maxTime = 30000) => {
  const backoffTime = Math.min(baseTime * Math.pow(2, errorCount), maxTime);
  // Añadir un poco de aleatoriedad (jitter) para evitar que todas las solicitudes se sincronicen
  return backoffTime + Math.random() * 1000;
};

// Función para limitar la frecuencia de llamadas a una función
const throttle = (func, limit) => {
  let inThrottle;
  let lastResult;
  
  return function() {
    const args = arguments;
    const context = this;
    
    if (!inThrottle) {
      inThrottle = true;
      lastResult = func.apply(context, args);
      
      setTimeout(() => {
        inThrottle = false;
      }, limit);
    }
    
    return lastResult;
  };
};

// Modificar la configuración de axios
const apiClient = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  },
  withCredentials: true,
  timeout: 30000 // 30 segundos
});

// Función auxiliar para determinar el tipo de usuario desde el token
const getUserTypeFromToken = () => {
  const token = localStorage.getItem('agthealth_token');
  let isIndependent = false;
 
  if (token) {
    try {
      const tokenData = JSON.parse(atob(token.split('.')[1]));
      isIndependent = tokenData.role === 'independent_professional';
    } catch (error) {
      console.error('Error al decodificar token:', error);
    }
  }
 
  return { isIndependent };
};

// Interceptor para añadir el token de autenticación a cada petición
apiClient.interceptors.request.use(
  async (config) => {
    try {
      const token = localStorage.getItem('agthealth_token');
      if (token) {
        // Reducir logging para evitar saturación de la consola
        if (process.env.NODE_ENV === 'development') {
          console.log(`Interceptor: Añadiendo token a la petición: ${config.url}`);
        }
        config.headers['Authorization'] = `Bearer ${token}`;
      } else {
        console.warn(`Interceptor: No hay token disponible para: ${config.url}`);
        
        // Solo redirigir si no es una ruta de autenticación
        const isAuthRoute = config.url.includes('/auth/') || 
                           config.url.includes('/independent-auth/') ||
                           config.url.includes('/login') ||
                           config.url.includes('/subscription');
                           
        if (!isAuthRoute && window.location.pathname !== '/login') {
          console.warn("Ruta protegida sin token, redirigiendo...");
          // Usar setTimeout para evitar problemas de redirección durante la ejecución
          setTimeout(() => {
            window.location.href = '/login';
          }, 100);
        }
      }
    } catch (error) {
      console.error('Error al obtener token de autenticación:', error);
    }
    return config;
  },
  (error) => {
    console.error("Error en interceptor de petición:", error);
    return Promise.reject(error);
  }
);

// Mejorar el interceptor de respuesta con manejo de errores 429
apiClient.interceptors.response.use(
  (response) => {
    // Reducir logging para evitar saturación de la consola
    if (process.env.NODE_ENV === 'development') {
      console.log(`Respuesta exitosa de ${response.config.url}:`, response.status);
    }
    
    // Resetear contador de errores para esta URL al tener éxito
    const url = response.config.url;
    if (apiCache.errorCount[url]) {
      apiCache.errorCount[url] = 0;
    }
    
    return response;
  },
  async (error) => {
    // Loggear errores importantes, excepto 401 que son comunes
    if (!(error.response && error.response.status === 401)) {
      console.error(`Error en respuesta de ${error.config?.url}:`, {
        status: error.response?.status,
        data: error.response?.data,
        error: error.message
      });
    }

    // Manejar error 429 (Too Many Requests) con reintento automático
    if (error.response && error.response.status === 429 && error.config) {
      const url = error.config.url;
      
      // Incrementar contador de errores para esta URL
      apiCache.errorCount[url] = (apiCache.errorCount[url] || 0) + 1;
      
      // Calcular tiempo de espera con backoff exponencial
      const retryAfter = error.response.headers['retry-after'] 
        ? parseInt(error.response.headers['retry-after']) * 1000 
        : calculateBackoffTime(apiCache.errorCount[url]);
      
      console.warn(`Error 429 detectado. Reintentando en ${retryAfter/1000} segundos.`);
      
      // Esperar y reintentar
      await new Promise(resolve => setTimeout(resolve, retryAfter));
      return apiClient(error.config);
    }

    // Si el error es 401 (no autorizado), redirigir al login
    if (error.response && error.response.status === 401) {
      console.warn("Sesión expirada o inválida, redirigiendo al login");
      localStorage.removeItem('agthealth_token');
      
      // Usar setTimeout para evitar problemas de redirección durante la ejecución
      setTimeout(() => {
        window.location.href = '/login';
      }, 100);
    }
    
    return Promise.reject(error);
  }
);

/**
 * Servicio para comunicación con el backend de AGT Health
 */
const agthealthService = {
  /**
   * Registra un nuevo usuario profesional con manejo de caché y errores mejorado
   * @param {Object} userData Datos del usuario
   * @returns {Promise<Object>} Respuesta con el usuario registrado
   */
  register: async (userData) => {
    try {
      console.log("Enviando petición de registro con datos:", userData);
      const response = await apiClient.post('/auth/register', userData);
      console.log("Respuesta de registro:", response.data);
      
      // Extraer y guardar el token de manera más robusta
      let token = null;
      if (response.data?.token) {
        token = response.data.token;
      } else if (response.data?.data?.token) {
        token = response.data.data.token;
      }
      
      if (token) {
        console.log("Token recibido en registro, guardando en localStorage");
        localStorage.setItem('agthealth_token', token);
        
        // Verificar que se guardó correctamente
        const savedToken = localStorage.getItem('agthealth_token');
        if (savedToken !== token) {
          console.error("Error: El token no se guardó correctamente");
        } else {
          console.log("Token guardado correctamente:", savedToken.substring(0, 15) + "...");
        }
      } else {
        console.warn("No se recibió token en la respuesta de registro");
      }
      
      return response.data;
    } catch (error) {
      console.error('Error al registrar usuario:', error);
      throw error;
    }
  },

  /**
   * Inicia sesión con credenciales con manejo de caché y errores mejorado
   * @param {Object} credentials Credenciales de usuario (email o identificationNumber, y password)
   * @returns {Promise<Object>} Respuesta con el token y datos del usuario
   */
  login: async (credentials) => {
    try {
      console.log("Enviando petición de login con credenciales:", credentials);
      console.log("URL completa:", `${API_URL}/auth/login`);
      
      const response = await apiClient.post('/auth/login', credentials);
      console.log("Respuesta completa de login:", response);
      
      // Extraer y guardar el token de manera más robusta
      let token = null;
      if (response.data?.token) {
        token = response.data.token;
      } else if (response.data?.data?.token) {
        token = response.data.data.token;
      }
      
      if (token) {
        console.log("Token encontrado, guardando en localStorage");
        localStorage.setItem('agthealth_token', token);
        
        // Verificar que se guardó correctamente
        const savedToken = localStorage.getItem('agthealth_token');
        if (savedToken !== token) {
          console.error("Error: El token no se guardó correctamente");
        } else {
          console.log("Token guardado correctamente:", savedToken.substring(0, 15) + "...");
        }
        
        // Limpiar caché al iniciar sesión
        agthealthService.clearCache();
      } else {
        console.warn("No se encontró token en la respuesta");
      }
      
      return response.data;
    } catch (error) {
      console.error('Error detallado al iniciar sesión:', error);
      console.error('Respuesta del servidor:', error.response?.data);
      console.error('Estado HTTP:', error.response?.status);
      throw error;
    }
  },

  /**
   * Cierra la sesión del usuario
   * @returns {Promise<Object>} Respuesta con el resultado
   */
  logout: async () => {
    try {
      console.log("Cerrando sesión, eliminando token del localStorage");
      localStorage.removeItem('agthealth_token');
      
      // Limpiar caché al cerrar sesión
      agthealthService.clearCache();
      
      const response = await apiClient.post('/auth/logout');
      return response.data;
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
      // Incluso si hay error, eliminar el token local
      localStorage.removeItem('agthealth_token');
      throw error;
    }
  },

  /**
   * Envía un correo para restablecer contraseña
   * @param {Object} data Datos para el restablecimiento
   * @returns {Promise<Object>} Respuesta con el resultado
   */
  forgotPassword: async (data) => {
    try {
      const response = await apiClient.post('/auth/forgot-password', data);
      return response.data;
    } catch (error) {
      console.error('Error al solicitar restablecimiento de contraseña:', error);
      throw error;
    }
  },

  /**
   * Restablece la contraseña con un token
   * @param {Object} data Datos para el restablecimiento
   * @returns {Promise<Object>} Respuesta con el resultado
   */
  resetPassword: async (data) => {
    try {
      const response = await apiClient.post('/auth/reset-password', data);
      return response.data;
    } catch (error) {
      console.error('Error al restablecer contraseña:', error);
      throw error;
    }
  },

  /**
   * Obtiene los centros de salud por país con control de caché
   * @param {string} countryCode Código del país (us, cl)
   * @returns {Promise<Object>} Respuesta con los centros de salud
   */
  getHealthCentersByCountry: async (countryCode) => {
    try {
      const cacheKey = `healthCenters_${countryCode}`;
      
      // Verificar si hay datos en caché de sessionStorage
      const cachedData = sessionStorage.getItem(cacheKey);
      if (cachedData) {
        return JSON.parse(cachedData);
      }
      
      const response = await apiClient.get(`/health-centers/${countryCode}`);
      
      // Guardar en sessionStorage
      if (response.data.success) {
        sessionStorage.setItem(cacheKey, JSON.stringify(response.data));
      }
      
      return response.data;
    } catch (error) {
      console.error('Error al obtener centros de salud:', error);
      throw error;
    }
  },

  /**
   * Verifica las credenciales médicas
   * @param {Object} credentialData Datos de las credenciales
   * @returns {Promise<Object>} Respuesta con el resultado de la verificación
   */
  verifyCredentials: async (credentialData) => {
    try {
      // Crear un FormData para enviar las imágenes
      const formData = new FormData();
      
      // Añadir las imágenes si existen
      if (credentialData.frontImage) {
        formData.append('frontImage', credentialData.frontImage);
      }
      
      if (credentialData.backImage) {
        formData.append('backImage', credentialData.backImage);
      }
      
      // Añadir otros datos
      formData.append('userId', credentialData.userId);
      formData.append('countryCode', credentialData.countryCode);
      formData.append('healthCenterId', credentialData.healthCenterId);
      
      const response = await apiClient.post('/credentials/verify', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error al verificar credenciales:', error);
      throw error;
    }
  },

  /**
   * Obtiene el perfil del usuario actual con control de caché
   * @param {boolean} forceReload Forzar recarga desde el servidor
   * @returns {Promise<Object>} Respuesta con los datos del perfil
   */
  getUserProfile: async (forceReload = false) => {
    try {
      const { isIndependent } = getUserTypeFromToken();
      const now = Date.now();
      
      // Si hay una promesa pendiente para esta solicitud, devolver esa promesa
      if (apiCache.pendingPromises.userProfile && !forceReload) {
        return apiCache.pendingPromises.userProfile;
      }
      
      // Si no se solicita forzar recarga, verificar caché
      if (!forceReload) {
        // Verificar si tenemos caché válida
        if (apiCache.userProfile && isCacheValid('userProfile')) {
          return { success: true, data: apiCache.userProfile };
        }
        
        // Verificar si podemos hacer una nueva solicitud (throttling)
        if (!canMakeRequest('userProfile')) {
          // Si no podemos hacer una solicitud, usar los datos en caché (incluso si están expirados)
          if (apiCache.userProfile) {
            return { success: true, data: apiCache.userProfile, fromCache: true };
          }
        }
      }
      
      // Crear una promesa para la solicitud actual
      const profilePromise = (async () => {
        try {
          // Usar la ruta correcta según el tipo de usuario
          const endpoint = isIndependent ? '/independent-users/profile' : '/users/profile';
          const response = await apiClient.get(endpoint);
          
          // Actualizar caché
          if (response.data.success && response.data.data) {
            apiCache.userProfile = response.data.data;
            apiCache.lastFetch.userProfile = now;
          }
          
          return response.data;
        } catch (error) {
          console.error('Error al obtener perfil de usuario:', error);
          
          // Si hay datos en caché, devolverlos en caso de error
          if (apiCache.userProfile) {
            return { success: true, data: apiCache.userProfile, fromCache: true };
          }
          
          return { 
            success: false, 
            error: error.message,
            data: null 
          };
        } finally {
          // Eliminar la promesa pendiente cuando se complete
          delete apiCache.pendingPromises.userProfile;
        }
      })();
      
      // Almacenar la promesa para posibles solicitudes simultáneas
      apiCache.pendingPromises.userProfile = profilePromise;
      
      return profilePromise;
    } catch (error) {
      console.error('Error inesperado en getUserProfile:', error);
      return { 
        success: false, 
        error: error.message,
        data: null 
      };
    }
  },

  /**
   * Actualiza el perfil del usuario
   * @param {Object} profileData Datos del perfil a actualizar
   * @returns {Promise<Object>} Respuesta con el perfil actualizado
   */
  updateUserProfile: async (profileData) => {
    try {
      const response = await apiClient.put('/users/profile', profileData);
      
      // Actualizar caché con los nuevos datos
      if (response.data.success && response.data.data) {
        apiCache.userProfile = response.data.data;
        apiCache.lastFetch.userProfile = Date.now();
      } else {
        // Invalidar caché si hay error
        apiCache.userProfile = null;
        apiCache.lastFetch.userProfile = null;
      }
      
      return response.data;
    } catch (error) {
      console.error('Error al actualizar perfil de usuario:', error);
      
      // Invalidar caché en caso de error
      apiCache.userProfile = null;
      apiCache.lastFetch.userProfile = null;
      
      throw error;
    }
  },

  /**
   * Verifica si un RUT o SSN ya está registrado
   * @param {Object} data Datos para la verificación
   * @returns {Promise<Object>} Respuesta con el resultado
   */
  checkIdentificationExists: async (data) => {
    try {
      const response = await apiClient.post('/auth/check-id', data);
      return response.data;
    } catch (error) {
      console.error('Error al verificar identificación:', error);
      throw error;
    }
  },

  /**
   * Crea un nuevo paciente
   * @param {Object} patientData Datos del paciente
   * @returns {Promise<Object>} Respuesta con el paciente creado
   */
  createPatient: async (patientData) => {
    try {
      const { isIndependent } = getUserTypeFromToken();
      
      // Usar la ruta correcta según el tipo de usuario
      const endpoint = isIndependent 
        ? '/independent-users/patients'
        : '/patients';
        
      const response = await apiClient.post(endpoint, patientData);
      
      // Invalidar caché de pacientes
      apiCache.patients = null;
      apiCache.lastFetch.patients = null;
      
      return response.data;
    } catch (error) {
      console.error('Error al crear paciente:', error);
      throw error;
    }
  },
  
  /**
   * Obtiene todos los pacientes del profesional con control de caché
   * @param {boolean} forceReload Forzar recarga desde el servidor
   * @returns {Promise<Object>} Respuesta con la lista de pacientes
   */
  getPatients: async (forceReload = false) => {
    try {
      const { isIndependent } = getUserTypeFromToken();
      const now = Date.now();
      
      // Si hay una promesa pendiente para esta solicitud, devolver esa promesa
      if (apiCache.pendingPromises.patients && !forceReload) {
        return apiCache.pendingPromises.patients;
      }
      
      // Si no se solicita forzar recarga, verificar caché
      if (!forceReload) {
        // Verificar si tenemos caché válida
        if (apiCache.patients && isCacheValid('patients')) {
          return { success: true, data: apiCache.patients };
        }
        
        // Verificar si podemos hacer una nueva solicitud (throttling)
        if (!canMakeRequest('patients')) {
          // Si no podemos hacer una solicitud, usar los datos en caché (incluso si están expirados)
          if (apiCache.patients) {
            return { success: true, data: apiCache.patients, fromCache: true };
          }
        }
      }
      
      // Crear una promesa para la solicitud actual
      const patientsPromise = (async () => {
        try {
          // Usar la ruta correcta según el tipo de usuario
          const endpoint = isIndependent 
            ? '/independent-users/patients'
            : '/patients';
            
          const response = await apiClient.get(endpoint);
          
          // Actualizar caché
          if (response.data.success && Array.isArray(response.data.data)) {
            apiCache.patients = response.data.data;
            apiCache.lastFetch.patients = now;
          }
          
          return response.data;
        } catch (error) {
          console.error('Error al obtener pacientes:', error);
          
          // Si hay datos en caché, devolverlos en caso de error
          if (apiCache.patients) {
            return { success: true, data: apiCache.patients, fromCache: true };
          }
          
          return { 
            success: false, 
            error: error.message,
            data: [] 
          };
        } finally {
          // Eliminar la promesa pendiente cuando se complete
          delete apiCache.pendingPromises.patients;
        }
      })();
      
      // Almacenar la promesa para posibles solicitudes simultáneas
      apiCache.pendingPromises.patients = patientsPromise;
      
      return patientsPromise;
    } catch (error) {
      console.error('Error inesperado en getPatients:', error);
      return { 
        success: false, 
        error: error.message,
        data: [] 
      };
    }
  },
  
  /**
   * Obtiene un paciente por su ID con control de caché
   * @param {string} patientId ID del paciente
   * @param {boolean} forceReload Forzar recarga desde el servidor
   * @returns {Promise<Object>} Respuesta con los datos del paciente
   */
  getPatientById: async (patientId, forceReload = false) => {
    try {
      const { isIndependent } = getUserTypeFromToken();
      const now = Date.now();
      
      // Si hay una promesa pendiente para esta solicitud, devolver esa promesa
      if (apiCache.pendingPromises[`patient_${patientId}`] && !forceReload) {
        return apiCache.pendingPromises[`patient_${patientId}`];
      }
      
      // Si no se solicita forzar recarga, verificar caché
      if (!forceReload) {
        // Inicializar el objeto si no existe
        if (!apiCache.lastFetch.patientDetails) {
          apiCache.lastFetch.patientDetails = {};
        }
        
        // Verificar si tenemos caché válida
        if (isCacheValid('patientDetails', patientId)) {
          const cachedPatient = sessionStorage.getItem(`patient_${patientId}`);
          if (cachedPatient) {
            try {
              return JSON.parse(cachedPatient);
            } catch (e) {
              console.error('Error al parsear paciente en caché:', e);
            }
          }
        }
        
        // Verificar si podemos hacer una nueva solicitud (throttling)
        if (!canMakeRequest('patientDetails', patientId)) {
          // Si no podemos hacer una solicitud, usar los datos en caché (incluso si están expirados)
          const cachedPatient = sessionStorage.getItem(`patient_${patientId}`);
          if (cachedPatient) {
            try {
              return JSON.parse(cachedPatient);
            } catch (e) {
              console.error('Error al parsear paciente en caché:', e);
            }
          }
        }
      }
      
      // Crear una promesa para la solicitud actual
      const patientPromise = (async () => {
        try {
          // Usar la ruta correcta según el tipo de usuario
          const endpoint = isIndependent 
            ? `/independent-users/patients/${patientId}`
            : `/patients/${patientId}`;
            
          const response = await apiClient.get(endpoint);
          
          // Actualizar caché
          if (response.data.success) {
            // Guardar en sessionStorage
            sessionStorage.setItem(`patient_${patientId}`, JSON.stringify(response.data));
            
            // Actualizar tiempo de última obtención
            apiCache.lastFetch.patientDetails[patientId] = now;
          }
          
          return response.data;
        } catch (error) {
          console.error('Error al obtener paciente por ID:', error);
          
          // Intentar usar caché si hay error
          const cachedPatient = sessionStorage.getItem(`patient_${patientId}`);
          if (cachedPatient) {
            try {
              return JSON.parse(cachedPatient);
            } catch (e) {
              console.error('Error al parsear paciente en caché:', e);
            }
          }
          
          return { 
            success: false, 
            error: error.message,
            data: null 
          };
        } finally {
          // Eliminar la promesa pendiente cuando se complete
          delete apiCache.pendingPromises[`patient_${patientId}`];
        }
      })();
      
      // Almacenar la promesa para posibles solicitudes simultáneas
      apiCache.pendingPromises[`patient_${patientId}`] = patientPromise;
      
      return patientPromise;
    } catch (error) {
      console.error('Error inesperado en getPatientById:', error);
      return { 
        success: false, 
        error: error.message,
        data: null 
      };
    }
  },
  
  /**
   * Actualiza los datos de un paciente
   * @param {string} patientId ID del paciente
   * @param {Object} patientData Datos a actualizar
   * @returns {Promise<Object>} Respuesta con el paciente actualizado
   */
  updatePatient: async (patientId, patientData) => {
    try {
      const { isIndependent } = getUserTypeFromToken();
      
      // Usar la ruta correcta según el tipo de usuario
      const endpoint = isIndependent 
        ? `/independent-users/patients/${patientId}`
        : `/patients/${patientId}`;
        
      const response = await apiClient.put(endpoint, patientData);
      
      // Actualizar caché
      if (response.data.success && response.data.data) {
        // Actualizar caché individual del paciente
        sessionStorage.setItem(`patient_${patientId}`, JSON.stringify(response.data));
        if (apiCache.lastFetch.patientDetails) {
          apiCache.lastFetch.patientDetails[patientId] = Date.now();
        }
        
        // Invalidar caché de lista de pacientes para forzar recarga
        apiCache.patients = null;
        apiCache.lastFetch.patients = null;
      }
      
      return response.data;
    } catch (error) {
      console.error('Error al actualizar paciente:', error);
      throw error;
    }
  },
  
  /**
   * Elimina un paciente
   * @param {string} patientId ID del paciente
   * @returns {Promise<Object>} Respuesta con el resultado
   */
  deletePatient: async (patientId) => {
    try {
      const { isIndependent } = getUserTypeFromToken();
      
      // Usar la ruta correcta según el tipo de usuario
      const endpoint = isIndependent 
        ? `/independent-users/patients/${patientId}`
        : `/patients/${patientId}`;
        
      const response = await apiClient.delete(endpoint);
      
      // Invalidar cachés
      sessionStorage.removeItem(`patient_${patientId}`);
      if (apiCache.lastFetch.patientDetails) {
        delete apiCache.lastFetch.patientDetails[patientId];
      }
      
      // Invalidar caché de lista de pacientes
      apiCache.patients = null;
      apiCache.lastFetch.patients = null;
      
      return response.data;
    } catch (error) {
      console.error('Error al eliminar paciente:', error);
      throw error;
    }
  },

  /**
   * Crea una nueva cita
   * @param {Object} appointmentData Datos de la cita
   * @returns {Promise<Object>} Respuesta con la cita creada
   */
  createAppointment: async (appointmentData) => {
    try {
      const response = await apiClient.post('/appointments', appointmentData);
      
      // Invalidar cachés de citas
      if (appointmentData.date) {
        const date = appointmentData.date.split('T')[0]; // Extraer solo la fecha
        if (apiCache.lastFetch.appointments) {
          delete apiCache.lastFetch.appointments[date];
          sessionStorage.removeItem(`appointments_${date}`);
        }
      }
      
      // Invalidar caché de citas por paciente
      if (appointmentData.patientId) {
        sessionStorage.removeItem(`appointments_patient_${appointmentData.patientId}`);
      }
      
      return response.data;
    } catch (error) {
      console.error('Error al crear cita:', error);
      throw error;
    }
  },
  
  /**
   * Obtiene todas las citas del profesional con control de caché
   * @param {boolean} forceReload Forzar recarga desde el servidor
   * @returns {Promise<Object>} Respuesta con la lista de citas
   */
  getAppointments: async (forceReload = false) => {
    try {
      const now = Date.now();
      const cacheKey = 'all';
      
      // Si hay una promesa pendiente para esta solicitud, devolver esa promesa
      if (apiCache.pendingPromises[`appointments_${cacheKey}`] && !forceReload) {
        return apiCache.pendingPromises[`appointments_${cacheKey}`];
      }
      
      // Si no se solicita forzar recarga, verificar caché
      if (!forceReload) {
        // Inicializar el objeto si no existe
        if (!apiCache.lastFetch.appointments) {
          apiCache.lastFetch.appointments = {};
        }
        
        // Verificar si tenemos caché válida
        if (isCacheValid('appointments', cacheKey)) {
          const cachedAppointments = sessionStorage.getItem(`appointments_${cacheKey}`);
          if (cachedAppointments) {
            try {
              return JSON.parse(cachedAppointments);
            } catch (e) {
              console.error('Error al parsear citas en caché:', e);
            }
          }
        }
        
        // Verificar si podemos hacer una nueva solicitud (throttling)
        if (!canMakeRequest('appointments', cacheKey)) {
          // Si no podemos hacer una solicitud, usar los datos en caché (incluso si están expirados)
          const cachedAppointments = sessionStorage.getItem(`appointments_${cacheKey}`);
          if (cachedAppointments) {
            try {
              return JSON.parse(cachedAppointments);
            } catch (e) {
              console.error('Error al parsear citas en caché:', e);
            }
          }
        }
      }
      
      // Crear una promesa para la solicitud actual
      const appointmentsPromise = (async () => {
        try {
          const response = await apiClient.get('/appointments');
          
          // Actualizar caché
          if (response.data.success) {
            // Guardar en sessionStorage
            sessionStorage.setItem(`appointments_${cacheKey}`, JSON.stringify(response.data));
            
            // Actualizar tiempo de última obtención
            apiCache.lastFetch.appointments[cacheKey] = now;
          }
          
          return response.data;
        } catch (error) {
          console.error('Error al obtener citas:', error);
          
          // Intentar usar caché si hay error
          const cachedAppointments = sessionStorage.getItem(`appointments_${cacheKey}`);
          if (cachedAppointments) {
            try {
              return JSON.parse(cachedAppointments);
            } catch (e) {
              console.error('Error al parsear citas en caché:', e);
            }
          }
          
          return { 
            success: false, 
            error: error.message,
            data: [] 
          };
        } finally {
          // Eliminar la promesa pendiente cuando se complete
          delete apiCache.pendingPromises[`appointments_${cacheKey}`];
        }
      })();
      
      // Almacenar la promesa para posibles solicitudes simultáneas
      apiCache.pendingPromises[`appointments_${cacheKey}`] = appointmentsPromise;
      
      return appointmentsPromise;
    } catch (error) {
      console.error('Error inesperado en getAppointments:', error);
      return { 
        success: false, 
        error: error.message,
        data: [] 
      };
    }
  },
  
  /**
   * Obtiene citas por rango de fechas con control de caché
   * @param {string} startDate Fecha de inicio en formato ISO
   * @param {string} endDate Fecha de fin en formato ISO
   * @param {boolean} forceReload Forzar recarga desde el servidor
   * @returns {Promise<Object>} Respuesta con la lista de citas
   */
  getAppointmentsByDateRange: async (startDate, endDate, forceReload = false) => {
    try {
      const now = Date.now();
      const cacheKey = `range_${startDate}_${endDate}`;
      
      // Si hay una promesa pendiente para esta solicitud, devolver esa promesa
      if (apiCache.pendingPromises[`appointments_${cacheKey}`] && !forceReload) {
        return apiCache.pendingPromises[`appointments_${cacheKey}`];
      }
      
      // Si no se solicita forzar recarga, verificar caché
      if (!forceReload) {
        // Inicializar el objeto si no existe
        if (!apiCache.lastFetch.appointments) {
          apiCache.lastFetch.appointments = {};
        }
        
        // Verificar si tenemos caché válida
        if (isCacheValid('appointments', cacheKey)) {
          const cachedAppointments = sessionStorage.getItem(`appointments_${cacheKey}`);
          if (cachedAppointments) {
            try {
              return JSON.parse(cachedAppointments);
            } catch (e) {
              console.error('Error al parsear citas en caché:', e);
            }
          }
        }
        
        // Verificar si podemos hacer una nueva solicitud (throttling)
        if (!canMakeRequest('appointments', cacheKey)) {
          // Si no podemos hacer una solicitud, usar los datos en caché (incluso si están expirados)
          const cachedAppointments = sessionStorage.getItem(`appointments_${cacheKey}`);
          if (cachedAppointments) {
            try {
              return JSON.parse(cachedAppointments);
            } catch (e) {
              console.error('Error al parsear citas en caché:', e);
            }
          }
        }
      }
      
      // Crear una promesa para la solicitud actual
      const appointmentsPromise = (async () => {
        try {
          const response = await apiClient.get(`/appointments/date-range?startDate=${startDate}&endDate=${endDate}`);
          
          // Actualizar caché
          if (response.data.success) {
            // Guardar en sessionStorage
            sessionStorage.setItem(`appointments_${cacheKey}`, JSON.stringify(response.data));
            
            // Actualizar tiempo de última obtención
            apiCache.lastFetch.appointments[cacheKey] = now;
          }
          
          return response.data;
        } catch (error) {
          console.error('Error al obtener citas por rango de fechas:', error);
          
          // Intentar usar caché si hay error
          const cachedAppointments = sessionStorage.getItem(`appointments_${cacheKey}`);
          if (cachedAppointments) {
            try {
              return JSON.parse(cachedAppointments);
            } catch (e) {
              console.error('Error al parsear citas en caché:', e);
            }
          }
          
          return { 
            success: false, 
            error: error.message,
            data: [] 
          };
        } finally {
          // Eliminar la promesa pendiente cuando se complete
          delete apiCache.pendingPromises[`appointments_${cacheKey}`];
        }
      })();
      
      // Almacenar la promesa para posibles solicitudes simultáneas
      apiCache.pendingPromises[`appointments_${cacheKey}`] = appointmentsPromise;
      
      return appointmentsPromise;
    } catch (error) {
      console.error('Error inesperado en getAppointmentsByDateRange:', error);
      return { 
        success: false, 
        error: error.message,
        data: [] 
      };
    }
  },
  
  /**
   * Obtiene una cita por su ID con control de caché
   * @param {string} appointmentId ID de la cita
   * @param {boolean} forceReload Forzar recarga desde el servidor
   * @returns {Promise<Object>} Respuesta con los datos de la cita
   */
  getAppointmentById: async (appointmentId, forceReload = false) => {
    try {
      const now = Date.now();
      const cacheKey = `id_${appointmentId}`;
      
      // Si hay una promesa pendiente para esta solicitud, devolver esa promesa
      if (apiCache.pendingPromises[`appointments_${cacheKey}`] && !forceReload) {
        return apiCache.pendingPromises[`appointments_${cacheKey}`];
      }
      
      // Si no se solicita forzar recarga, verificar caché
      if (!forceReload) {
        // Inicializar el objeto si no existe
        if (!apiCache.lastFetch.appointments) {
          apiCache.lastFetch.appointments = {};
        }
        
        // Verificar si tenemos caché válida
        if (isCacheValid('appointments', cacheKey)) {
          const cachedAppointment = sessionStorage.getItem(`appointments_${cacheKey}`);
          if (cachedAppointment) {
            try {
              return JSON.parse(cachedAppointment);
            } catch (e) {
              console.error('Error al parsear cita en caché:', e);
            }
          }
        }
        
        // Verificar si podemos hacer una nueva solicitud (throttling)
        if (!canMakeRequest('appointments', cacheKey)) {
          // Si no podemos hacer una solicitud, usar los datos en caché (incluso si están expirados)
          const cachedAppointment = sessionStorage.getItem(`appointments_${cacheKey}`);
          if (cachedAppointment) {
            try {
              return JSON.parse(cachedAppointment);
            } catch (e) {
              console.error('Error al parsear cita en caché:', e);
            }
          }
        }
      }
      
      // Crear una promesa para la solicitud actual
      const appointmentPromise = (async () => {
        try {
          const response = await apiClient.get(`/appointments/${appointmentId}`);
          
          // Actualizar caché
          if (response.data.success) {
            // Guardar en sessionStorage
            sessionStorage.setItem(`appointments_${cacheKey}`, JSON.stringify(response.data));
            
            // Actualizar tiempo de última obtención
            apiCache.lastFetch.appointments[cacheKey] = now;
          }
          
          return response.data;
        } catch (error) {
          console.error('Error al obtener cita por ID:', error);
          
          // Intentar usar caché si hay error
          const cachedAppointment = sessionStorage.getItem(`appointments_${cacheKey}`);
          if (cachedAppointment) {
            try {
              return JSON.parse(cachedAppointment);
            } catch (e) {
              console.error('Error al parsear cita en caché:', e);
            }
          }
          
          return { 
            success: false, 
            error: error.message,
            data: null 
          };
        } finally {
          // Eliminar la promesa pendiente cuando se complete
          delete apiCache.pendingPromises[`appointments_${cacheKey}`];
        }
      })();
      
      // Almacenar la promesa para posibles solicitudes simultáneas
      apiCache.pendingPromises[`appointments_${cacheKey}`] = appointmentPromise;
      
      return appointmentPromise;
    } catch (error) {
      console.error('Error inesperado en getAppointmentById:', error);
      return { 
        success: false, 
        error: error.message,
        data: null 
      };
    }
  },
  
  /**
   * Obtiene citas de un paciente con control de caché
   * @param {string} patientId ID del paciente
   * @param {boolean} forceReload Forzar recarga desde el servidor
   * @returns {Promise<Object>} Respuesta con la lista de citas
   */
  getAppointmentsByPatient: async (patientId, forceReload = false) => {
    try {
      const now = Date.now();
      const cacheKey = `patient_${patientId}`;
      
      // Si hay una promesa pendiente para esta solicitud, devolver esa promesa
      if (apiCache.pendingPromises[`appointments_${cacheKey}`] && !forceReload) {
        return apiCache.pendingPromises[`appointments_${cacheKey}`];
      }
      
      // Si no se solicita forzar recarga, verificar caché
      if (!forceReload) {
        // Inicializar el objeto si no existe
        if (!apiCache.lastFetch.appointments) {
          apiCache.lastFetch.appointments = {};
        }
        
        // Verificar si tenemos caché válida
        if (isCacheValid('appointments', cacheKey)) {
          const cachedAppointments = sessionStorage.getItem(`appointments_${cacheKey}`);
          if (cachedAppointments) {
            try {
              return JSON.parse(cachedAppointments);
            } catch (e) {
              console.error('Error al parsear citas en caché:', e);
            }
          }
        }
        
        // Verificar si podemos hacer una nueva solicitud (throttling)
        if (!canMakeRequest('appointments', cacheKey)) {
          // Si no podemos hacer una solicitud, usar los datos en caché (incluso si están expirados)
          const cachedAppointments = sessionStorage.getItem(`appointments_${cacheKey}`);
          if (cachedAppointments) {
            try {
              return JSON.parse(cachedAppointments);
            } catch (e) {
              console.error('Error al parsear citas en caché:', e);
            }
          }
        }
      }
      
      // Crear una promesa para la solicitud actual
      const appointmentsPromise = (async () => {
        try {
          const response = await apiClient.get(`/appointments/patient/${patientId}`);
          
          // Actualizar caché
          if (response.data.success) {
            // Guardar en sessionStorage
            sessionStorage.setItem(`appointments_${cacheKey}`, JSON.stringify(response.data));
            
            // Actualizar tiempo de última obtención
            apiCache.lastFetch.appointments[cacheKey] = now;
          }
          
          return response.data;
        } catch (error) {
          console.error('Error al obtener citas del paciente:', error);
          
          // Intentar usar caché si hay error
          const cachedAppointments = sessionStorage.getItem(`appointments_${cacheKey}`);
          if (cachedAppointments) {
            try {
              return JSON.parse(cachedAppointments);
            } catch (e) {
              console.error('Error al parsear citas en caché:', e);
            }
          }
          
          return { 
            success: false, 
            error: error.message,
            data: [] 
          };
        } finally {
          // Eliminar la promesa pendiente cuando se complete
          delete apiCache.pendingPromises[`appointments_${cacheKey}`];
        }
      })();
      
      // Almacenar la promesa para posibles solicitudes simultáneas
      apiCache.pendingPromises[`appointments_${cacheKey}`] = appointmentsPromise;
      
      return appointmentsPromise;
    } catch (error) {
      console.error('Error inesperado en getAppointmentsByPatient:', error);
      return { 
        success: false, 
        error: error.message,
        data: [] 
      };
    }
  },
  
  /**
   * Actualiza una cita
   * @param {string} appointmentId ID de la cita
   * @param {Object} appointmentData Datos a actualizar
   * @returns {Promise<Object>} Respuesta con la cita actualizada
   */
  updateAppointment: async (appointmentId, appointmentData) => {
    try {
      const response = await apiClient.put(`/appointments/${appointmentId}`, appointmentData);
      
      // Invalidar cachés relacionadas con citas
      if (!apiCache.lastFetch.appointments) {
        apiCache.lastFetch.appointments = {};
      }
      
      // Invalidar caché específica de esta cita
      sessionStorage.removeItem(`appointments_id_${appointmentId}`);
      
      // Invalidar caché de todas las citas
      sessionStorage.removeItem('appointments_all');
      delete apiCache.lastFetch.appointments['all'];
      
      // Invalidar caché de citas por paciente
      if (appointmentData.patientId) {
        sessionStorage.removeItem(`appointments_patient_${appointmentData.patientId}`);
        delete apiCache.lastFetch.appointments[`patient_${appointmentData.patientId}`];
      }
      
      // Invalidar caché de citas por fecha
      if (appointmentData.date) {
        const date = appointmentData.date.split('T')[0]; // Extraer solo la fecha
        
        // Buscar y eliminar cualquier caché de rango que pueda contener esta fecha
        Object.keys(apiCache.lastFetch.appointments).forEach(key => {
          if (key.startsWith('range_')) {
            delete apiCache.lastFetch.appointments[key];
            sessionStorage.removeItem(`appointments_${key}`);
          }
        });
      }
      
      return response.data;
    } catch (error) {
      console.error('Error al actualizar cita:', error);
      throw error;
    }
  },
  
  /**
   * Elimina una cita
   * @param {string} appointmentId ID de la cita
   * @returns {Promise<Object>} Respuesta con el resultado
   */
  deleteAppointment: async (appointmentId) => {
    try {
      // Primero obtener los datos de la cita para saber qué cachés invalidar
      let patientId, date;
      try {
        const appointmentData = sessionStorage.getItem(`appointments_id_${appointmentId}`);
        if (appointmentData) {
          const parsed = JSON.parse(appointmentData);
          if (parsed.data) {
            patientId = parsed.data.patientId;
            date = parsed.data.date;
          }
        }
      } catch (e) {
        console.error('Error al obtener datos de cita para caché:', e);
      }
      
      const response = await apiClient.delete(`/appointments/${appointmentId}`);
      
      // Invalidar cachés relacionadas con citas
      if (!apiCache.lastFetch.appointments) {
        apiCache.lastFetch.appointments = {};
      }
      
      // Invalidar caché específica de esta cita
      sessionStorage.removeItem(`appointments_id_${appointmentId}`);
      
      // Invalidar caché de todas las citas
      sessionStorage.removeItem('appointments_all');
      delete apiCache.lastFetch.appointments['all'];
      
      // Invalidar caché de citas por paciente
      if (patientId) {
        sessionStorage.removeItem(`appointments_patient_${patientId}`);
        delete apiCache.lastFetch.appointments[`patient_${patientId}`];
      }
      
      // Invalidar caché de citas por fecha
      if (date) {
        // Buscar y eliminar cualquier caché de rango que pueda contener esta fecha
        Object.keys(apiCache.lastFetch.appointments).forEach(key => {
          if (key.startsWith('range_')) {
            delete apiCache.lastFetch.appointments[key];
            sessionStorage.removeItem(`appointments_${key}`);
          }
        });
      }
      
      return response.data;
    } catch (error) {
      console.error('Error al eliminar cita:', error);
      throw error;
    }
  },

  /**
   * Envía un recordatorio manual para una cita
   * @param {string} appointmentId ID de la cita
   * @returns {Promise<Object>} Respuesta con el resultado
   */
  sendAppointmentReminder: async (appointmentId) => {
    try {
      const response = await apiClient.post(`/reminders/appointments/${appointmentId}/send`);
      return response.data;
    } catch (error) {
      console.error('Error al enviar recordatorio:', error);
      throw error;
    }
  },
  
  /**
   * Activa o desactiva los recordatorios para una cita
   * @param {string} appointmentId ID de la cita
   * @param {boolean} enable Activar o desactivar recordatorios
   * @returns {Promise<Object>} Respuesta con el resultado
   */
  toggleAppointmentReminders: async (appointmentId, enable) => {
    try {
      const response = await apiClient.put(`/reminders/appointments/${appointmentId}/toggle`, {
        enableReminders: enable
      });
      
      // Invalidar caché específica de esta cita
      sessionStorage.removeItem(`appointments_id_${appointmentId}`);
      
      return response.data;
    } catch (error) {
      console.error('Error al cambiar estado de recordatorios:', error);
      throw error;
    }
  },

  /**
   * Busca pacientes con filtros y control de caché
   * @param {Object} filters Objeto con los filtros de búsqueda
   * @param {number} page Número de página
   * @param {number} limit Límite de resultados por página
   * @param {boolean} forceReload Forzar recarga desde el servidor
   * @returns {Promise<Object>} Respuesta con los pacientes filtrados
   */
  searchPatients: async (filters = {}, page = 1, limit = 20, forceReload = false) => {
    try {
      const { isIndependent } = getUserTypeFromToken();
      
      const queryParams = new URLSearchParams({
        page,
        limit,
        ...filters
      }).toString();
      
      const cacheKey = `search_${queryParams}`;
      
      // Si hay una promesa pendiente para esta solicitud, devolver esa promesa
      if (apiCache.pendingPromises[cacheKey] && !forceReload) {
        return apiCache.pendingPromises[cacheKey];
      }
      
      // Si no se solicita forzar recarga, verificar caché
      if (!forceReload) {
        const cachedResults = sessionStorage.getItem(cacheKey);
        if (cachedResults) {
          try {
            return JSON.parse(cachedResults);
          } catch (e) {
            console.error('Error al parsear resultados de búsqueda en caché:', e);
          }
        }
      }
    
      // Crear una promesa para la solicitud actual
      const searchPromise = (async () => {
        try {
          // Usar la ruta correcta según el tipo de usuario
          const endpoint = isIndependent 
            ? `/independent-users/patients/search?${queryParams}`
            : `/patients/search?${queryParams}`;
            
          console.log(`Buscando pacientes con parámetros: ${queryParams}`);
          const response = await apiClient.get(endpoint);
          
          // Guardar en caché
          if (response.data.success) {
            sessionStorage.setItem(cacheKey, JSON.stringify(response.data));
          }
          
          return response.data;
        } catch (error) {
          console.error('Error al buscar pacientes:', error);
          
          // Intentar usar caché si hay error
          const cachedResults = sessionStorage.getItem(cacheKey);
          if (cachedResults) {
            try {
              return JSON.parse(cachedResults);
            } catch (e) {
              console.error('Error al parsear resultados de búsqueda en caché:', e);
            }
          }
          
          throw error;
        } finally {
          // Eliminar la promesa pendiente cuando se complete
          delete apiCache.pendingPromises[cacheKey];
        }
      })();
      
      // Almacenar la promesa para posibles solicitudes simultáneas
      apiCache.pendingPromises[cacheKey] = searchPromise;
      
      return searchPromise;
    } catch (error) {
      console.error('Error inesperado en searchPatients:', error);
      throw error;
    }
  },
    
  /**
   * Obtiene las enfermedades crónicas más comunes para autocompletar con control de caché
   * @returns {Promise<Object>} Respuesta con la lista de enfermedades
   */
  getCommonChronicDiseases: async () => {
    try {
      const { isIndependent } = getUserTypeFromToken();
      const cacheKey = 'common_diseases';
      
      // Verificar si hay datos en caché
      const cachedDiseases = sessionStorage.getItem(cacheKey);
      if (cachedDiseases) {
        try {
          return JSON.parse(cachedDiseases);
        } catch (e) {
          console.error('Error al parsear enfermedades en caché:', e);
        }
      }
      
      // Usar la ruta correcta según el tipo de usuario
      const endpoint = isIndependent 
        ? '/independent-users/patients/common-diseases'
        : '/patients/common-diseases';
        
      const response = await apiClient.get(endpoint);
      
      // Guardar en caché
      if (response.data.success) {
        sessionStorage.setItem(cacheKey, JSON.stringify(response.data));
      }
      
      return response.data;
    } catch (error) {
      console.error('Error al obtener enfermedades comunes:', error);
      throw error;
    }
  },

  /**
   * Guarda una nota (crea o actualiza)
   * @param {Object} noteData Datos de la nota
   * @returns {Promise<Object>} Respuesta con la nota guardada
   */
  saveNote: async (noteData) => {
    try {
      const response = await apiClient.post('/notes', noteData);
      
      // Invalidar caché de notas
      if (!apiCache.lastFetch.notes) {
        apiCache.lastFetch.notes = {};
      }
      
      // Invalidar caché de todas las notas
      delete apiCache.lastFetch.notes['all'];
      sessionStorage.removeItem('notes_all');
      
      return response.data;
    } catch (error) {
      console.error('Error al guardar nota:', error);
      throw error;
    }
  },

  /**
   * Obtiene todas las notas del usuario con control de caché
   * @param {boolean} forceReload Forzar recarga desde el servidor
   * @returns {Promise<Object>} Respuesta con la lista de notas
   */
  getUserNotes: async (forceReload = false) => {
    try {
      const now = Date.now();
      const cacheKey = 'all';
      
      // Si hay una promesa pendiente para esta solicitud, devolver esa promesa
      if (apiCache.pendingPromises[`notes_${cacheKey}`] && !forceReload) {
        return apiCache.pendingPromises[`notes_${cacheKey}`];
      }
      
      // Si no se solicita forzar recarga, verificar caché
      if (!forceReload) {
        // Inicializar el objeto si no existe
        if (!apiCache.lastFetch.notes) {
          apiCache.lastFetch.notes = {};
        }
        
        // Verificar si tenemos caché válida
        if (isCacheValid('notes', cacheKey)) {
          const cachedNotes = sessionStorage.getItem(`notes_${cacheKey}`);
          if (cachedNotes) {
            try {
              return JSON.parse(cachedNotes);
            } catch (e) {
              console.error('Error al parsear notas en caché:', e);
            }
          }
        }
        
        // Verificar si podemos hacer una nueva solicitud (throttling)
        if (!canMakeRequest('notes', cacheKey)) {
          // Si no podemos hacer una solicitud, usar los datos en caché (incluso si están expirados)
          const cachedNotes = sessionStorage.getItem(`notes_${cacheKey}`);
          if (cachedNotes) {
            try {
              return JSON.parse(cachedNotes);
            } catch (e) {
              console.error('Error al parsear notas en caché:', e);
            }
          }
        }
      }
      
      // Crear una promesa para la solicitud actual
      const notesPromise = (async () => {
        try {
          const response = await apiClient.get('/notes');
          
          // Actualizar caché
          if (response.data.success) {
            // Guardar en sessionStorage
            sessionStorage.setItem(`notes_${cacheKey}`, JSON.stringify(response.data));
            
            // Actualizar tiempo de última obtención
            apiCache.lastFetch.notes[cacheKey] = now;
          }
          
          return response.data;
        } catch (error) {
          console.error('Error al obtener notas del usuario:', error);
          
          // Intentar usar caché si hay error
          const cachedNotes = sessionStorage.getItem(`notes_${cacheKey}`);
          if (cachedNotes) {
            try {
              return JSON.parse(cachedNotes);
            } catch (e) {
              console.error('Error al parsear notas en caché:', e);
            }
          }
          
          return { 
            success: false, 
            error: error.message,
            data: [] 
          };
        } finally {
          // Eliminar la promesa pendiente cuando se complete
          delete apiCache.pendingPromises[`notes_${cacheKey}`];
        }
      })();
      
      // Almacenar la promesa para posibles solicitudes simultáneas
      apiCache.pendingPromises[`notes_${cacheKey}`] = notesPromise;
      
      return notesPromise;
    } catch (error) {
      console.error('Error inesperado en getUserNotes:', error);
      return { 
        success: false, 
        error: error.message,
        data: [] 
      };
    }
  },

  /**
   * Obtiene una nota por su ID con control de caché
   * @param {string} noteId ID de la nota
   * @param {boolean} forceReload Forzar recarga desde el servidor
   * @returns {Promise<Object>} Respuesta con los datos de la nota
   */
  getNoteById: async (noteId, forceReload = false) => {
    try {
      const now = Date.now();
      const cacheKey = `id_${noteId}`;
      
      // Si hay una promesa pendiente para esta solicitud, devolver esa promesa
      if (apiCache.pendingPromises[`notes_${cacheKey}`] && !forceReload) {
        return apiCache.pendingPromises[`notes_${cacheKey}`];
      }
      
      // Si no se solicita forzar recarga, verificar caché
      if (!forceReload) {
        // Inicializar el objeto si no existe
        if (!apiCache.lastFetch.notes) {
          apiCache.lastFetch.notes = {};
        }
        
        // Verificar si tenemos caché válida
        if (isCacheValid('notes', cacheKey)) {
          const cachedNote = sessionStorage.getItem(`notes_${cacheKey}`);
          if (cachedNote) {
            try {
              return JSON.parse(cachedNote);
            } catch (e) {
              console.error('Error al parsear nota en caché:', e);
            }
          }
        }
        
        // Verificar si podemos hacer una nueva solicitud (throttling)
        if (!canMakeRequest('notes', cacheKey)) {
          // Si no podemos hacer una solicitud, usar los datos en caché (incluso si están expirados)
          const cachedNote = sessionStorage.getItem(`notes_${cacheKey}`);
          if (cachedNote) {
            try {
              return JSON.parse(cachedNote);
            } catch (e) {
              console.error('Error al parsear nota en caché:', e);
            }
          }
        }
      }
      
      // Crear una promesa para la solicitud actual
      const notePromise = (async () => {
        try {
          const response = await apiClient.get(`/notes/${noteId}`);
          
          // Actualizar caché
          if (response.data.success) {
            // Guardar en sessionStorage
            sessionStorage.setItem(`notes_${cacheKey}`, JSON.stringify(response.data));
            
            // Actualizar tiempo de última obtención
            apiCache.lastFetch.notes[cacheKey] = now;
          }
          
          return response.data;
        } catch (error) {
          console.error('Error al obtener nota por ID:', error);
          
          // Intentar usar caché si hay error
          const cachedNote = sessionStorage.getItem(`notes_${cacheKey}`);
          if (cachedNote) {
            try {
              return JSON.parse(cachedNote);
            } catch (e) {
              console.error('Error al parsear nota en caché:', e);
            }
          }
          
          return { 
            success: false, 
            error: error.message,
            data: null 
          };
        } finally {
          // Eliminar la promesa pendiente cuando se complete
          delete apiCache.pendingPromises[`notes_${cacheKey}`];
        }
      })();
      
      // Almacenar la promesa para posibles solicitudes simultáneas
      apiCache.pendingPromises[`notes_${cacheKey}`] = notePromise;
      
      return notePromise;
    } catch (error) {
      console.error('Error inesperado en getNoteById:', error);
      return { 
        success: false, 
        error: error.message,
        data: null 
      };
    }
  },

  /**
   * Elimina una nota
   * @param {string} noteId ID de la nota
   * @returns {Promise<Object>} Respuesta con el resultado
   */
  deleteNote: async (noteId) => {
    try {
      const response = await apiClient.delete(`/notes/${noteId}`);
      
      // Invalidar cachés relacionadas
      if (!apiCache.lastFetch.notes) {
        apiCache.lastFetch.notes = {};
      }
      
      // Invalidar caché específica de esta nota
      sessionStorage.removeItem(`notes_id_${noteId}`);
      delete apiCache.lastFetch.notes[`id_${noteId}`];
      
      // Invalidar caché de todas las notas
      sessionStorage.removeItem('notes_all');
      delete apiCache.lastFetch.notes['all'];
      
      return response.data;
    } catch (error) {
      console.error('Error al eliminar nota:', error);
      throw error;
    }
  },

  /**
   * Crea una tarea desde una nota usando AGT Task
   * @param {string} noteId ID de la nota
   * @returns {Promise<Object>} Respuesta con el resultado
   */
  createTaskFromNote: async (noteId) => {
    try {
      console.log(`Creando tarea desde nota ${noteId} usando AGT Task`);
      const response = await apiClient.post(`/notes/${noteId}/create-task`);
      
      // Invalidar caché de tareas
      if (!apiCache.lastFetch.tasks) {
        apiCache.lastFetch.tasks = {};
      }
      
      // Invalidar caché de todas las tareas
      delete apiCache.lastFetch.tasks['all'];
      sessionStorage.removeItem('tasks_all');
      
      console.log('Tarea creada exitosamente:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error al crear tarea desde nota:', error);
      
      // Extraer mensaje de error específico si está disponible
      const errorMessage = error.response?.data?.message || 
                          'Error al procesar la nota con AGT Task';
      
      throw new Error(errorMessage);
    }
  },

  /**
   * Crea una nueva tarea
   * @param {Object} taskData Datos de la tarea
   * @returns {Promise<Object>} Respuesta con la tarea creada
   */
  createTask: async (taskData) => {
    try {
      const response = await apiClient.post('/tasks', taskData);
      
      // Invalidar caché de tareas
      if (!apiCache.lastFetch.tasks) {
        apiCache.lastFetch.tasks = {};
      }
      
      // Invalidar caché de todas las tareas
      delete apiCache.lastFetch.tasks['all'];
      sessionStorage.removeItem('tasks_all');
      
      // Invalidar caché de widget
      delete apiCache.lastFetch.tasks['widget'];
      sessionStorage.removeItem('tasks_widget');
      
      return response.data;
    } catch (error) {
      console.error('Error al crear tarea:', error);
      throw error;
    }
  },
  
  /**
   * Obtiene todas las tareas del usuario con control de caché
   * @param {Object} filters Filtros opcionales (ej: completed)
   * @param {boolean} forceReload Forzar recarga desde el servidor
   * @returns {Promise<Object>} Respuesta con la lista de tareas
   */
  getUserTasks: async (filters = {}, forceReload = false) => {
    try {
      const now = Date.now();
      const queryParams = new URLSearchParams(filters).toString();
      const cacheKey = queryParams ? `filters_${queryParams}` : 'all';
      
      // Si hay una promesa pendiente para esta solicitud, devolver esa promesa
      if (apiCache.pendingPromises[`tasks_${cacheKey}`] && !forceReload) {
        return apiCache.pendingPromises[`tasks_${cacheKey}`];
      }
      
      // Si no se solicita forzar recarga, verificar caché
      if (!forceReload) {
        // Inicializar el objeto si no existe
        if (!apiCache.lastFetch.tasks) {
          apiCache.lastFetch.tasks = {};
        }
        
        // Verificar si tenemos caché válida
        if (isCacheValid('tasks', cacheKey)) {
          const cachedTasks = sessionStorage.getItem(`tasks_${cacheKey}`);
          if (cachedTasks) {
            try {
              return JSON.parse(cachedTasks);
            } catch (e) {
              console.error('Error al parsear tareas en caché:', e);
            }
          }
        }
        
        // Verificar si podemos hacer una nueva solicitud (throttling)
        if (!canMakeRequest('tasks', cacheKey)) {
          // Si no podemos hacer una solicitud, usar los datos en caché (incluso si están expirados)
          const cachedTasks = sessionStorage.getItem(`tasks_${cacheKey}`);
          if (cachedTasks) {
            try {
              return JSON.parse(cachedTasks);
            } catch (e) {
              console.error('Error al parsear tareas en caché:', e);
            }
          }
        }
      }
      
      // Crear una promesa para la solicitud actual
      const tasksPromise = (async () => {
        try {
          const url = queryParams ? `/tasks?${queryParams}` : '/tasks';
          const response = await apiClient.get(url);
          
          // Actualizar caché
          if (response.data.success) {
            // Guardar en sessionStorage
            sessionStorage.setItem(`tasks_${cacheKey}`, JSON.stringify(response.data));
            
            // Actualizar tiempo de última obtención
            apiCache.lastFetch.tasks[cacheKey] = now;
          }
          
          return response.data;
        } catch (error) {
          console.error('Error al obtener tareas del usuario:', error);
          
          // Intentar usar caché si hay error
          const cachedTasks = sessionStorage.getItem(`tasks_${cacheKey}`);
          if (cachedTasks) {
            try {
              return JSON.parse(cachedTasks);
            } catch (e) {
              console.error('Error al parsear tareas en caché:', e);
            }
          }
          
          return { 
            success: false, 
            error: error.message,
            data: [] 
          };
        } finally {
          // Eliminar la promesa pendiente cuando se complete
          delete apiCache.pendingPromises[`tasks_${cacheKey}`];
        }
      })();
      
      // Almacenar la promesa para posibles solicitudes simultáneas
      apiCache.pendingPromises[`tasks_${cacheKey}`] = tasksPromise;
      
      return tasksPromise;
    } catch (error) {
      console.error('Error inesperado en getUserTasks:', error);
      return { 
        success: false, 
        error: error.message,
        data: [] 
      };
    }
  },
  
  /**
   * Obtiene tareas para mostrar en el widget con control de caché
   * @param {number} limit Número máximo de tareas a obtener
   * @param {boolean} forceReload Forzar recarga desde el servidor
   * @returns {Promise<Object>} Respuesta con la lista de tareas
   */
  getTasksForWidget: async (limit = 5, forceReload = false) => {
    try {
      const now = Date.now();
      const cacheKey = 'widget';
      
      // Si hay una promesa pendiente para esta solicitud, devolver esa promesa
      if (apiCache.pendingPromises[`tasks_${cacheKey}`] && !forceReload) {
        return apiCache.pendingPromises[`tasks_${cacheKey}`];
      }
      
      // Si no se solicita forzar recarga, verificar caché
      if (!forceReload) {
        // Inicializar el objeto si no existe
        if (!apiCache.lastFetch.tasks) {
          apiCache.lastFetch.tasks = {};
        }
        
        // Verificar si tenemos caché válida
        if (isCacheValid('tasks', cacheKey)) {
          const cachedTasks = sessionStorage.getItem(`tasks_${cacheKey}`);
          if (cachedTasks) {
            try {
              return JSON.parse(cachedTasks);
            } catch (e) {
              console.error('Error al parsear tareas de widget en caché:', e);
            }
          }
        }
        
        // Verificar si podemos hacer una nueva solicitud (throttling)
        if (!canMakeRequest('tasks', cacheKey)) {
          // Si no podemos hacer una solicitud, usar los datos en caché (incluso si están expirados)
          const cachedTasks = sessionStorage.getItem(`tasks_${cacheKey}`);
          if (cachedTasks) {
            try {
              return JSON.parse(cachedTasks);
            } catch (e) {
              console.error('Error al parsear tareas de widget en caché:', e);
            }
          }
        }
      }
      
      // Crear una promesa para la solicitud actual
      const tasksPromise = (async () => {
        try {
          const response = await apiClient.get(`/tasks/widget?limit=${limit}`);
          
          // Actualizar caché
          if (response.data.success) {
            // Guardar en sessionStorage
            sessionStorage.setItem(`tasks_${cacheKey}`, JSON.stringify(response.data));
            
            // Actualizar tiempo de última obtención
            apiCache.lastFetch.tasks[cacheKey] = now;
          }
          
          return response.data;
        } catch (error) {
          console.error('Error al obtener tareas para widget:', error);
          
          // Intentar usar caché si hay error
          const cachedTasks = sessionStorage.getItem(`tasks_${cacheKey}`);
          if (cachedTasks) {
            try {
              return JSON.parse(cachedTasks);
            } catch (e) {
              console.error('Error al parsear tareas de widget en caché:', e);
            }
          }
          
          return { 
            success: false, 
            error: error.message,
            data: [] 
          };
        } finally {
          // Eliminar la promesa pendiente cuando se complete
          delete apiCache.pendingPromises[`tasks_${cacheKey}`];
        }
      })();
      
      // Almacenar la promesa para posibles solicitudes simultáneas
      apiCache.pendingPromises[`tasks_${cacheKey}`] = tasksPromise;
      
      return tasksPromise;
    } catch (error) {
      console.error('Error inesperado en getTasksForWidget:', error);
      return { 
        success: false, 
        error: error.message,
        data: [] 
      };
    }
  },
  
  /**
   * Obtiene una tarea por su ID con control de caché
   * @param {string} taskId ID de la tarea
   * @param {boolean} forceReload Forzar recarga desde el servidor
   * @returns {Promise<Object>} Respuesta con los datos de la tarea
   */
  getTaskById: async (taskId, forceReload = false) => {
    try {
      const now = Date.now();
      const cacheKey = `id_${taskId}`;
      
      // Si hay una promesa pendiente para esta solicitud, devolver esa promesa
      if (apiCache.pendingPromises[`tasks_${cacheKey}`] && !forceReload) {
        return apiCache.pendingPromises[`tasks_${cacheKey}`];
      }
      
      // Si no se solicita forzar recarga, verificar caché
      if (!forceReload) {
        // Inicializar el objeto si no existe
        if (!apiCache.lastFetch.tasks) {
          apiCache.lastFetch.tasks = {};
        }
        
        // Verificar si tenemos caché válida
        if (isCacheValid('tasks', cacheKey)) {
          const cachedTask = sessionStorage.getItem(`tasks_${cacheKey}`);
          if (cachedTask) {
            try {
              return JSON.parse(cachedTask);
            } catch (e) {
              console.error('Error al parsear tarea en caché:', e);
            }
          }
        }
        
        // Verificar si podemos hacer una nueva solicitud (throttling)
        if (!canMakeRequest('tasks', cacheKey)) {
          // Si no podemos hacer una solicitud, usar los datos en caché (incluso si están expirados)
          const cachedTask = sessionStorage.getItem(`tasks_${cacheKey}`);
          if (cachedTask) {
            try {
              return JSON.parse(cachedTask);
            } catch (e) {
              console.error('Error al parsear tarea en caché:', e);
            }
          }
        }
      }
      
      // Crear una promesa para la solicitud actual
      const taskPromise = (async () => {
        try {
          const response = await apiClient.get(`/tasks/${taskId}`);
          
          // Actualizar caché
          if (response.data.success) {
            // Guardar en sessionStorage
            sessionStorage.setItem(`tasks_${cacheKey}`, JSON.stringify(response.data));
            
            // Actualizar tiempo de última obtención
            apiCache.lastFetch.tasks[cacheKey] = now;
          }
          
          return response.data;
        } catch (error) {
          console.error('Error al obtener tarea por ID:', error);
          
          // Intentar usar caché si hay error
          const cachedTask = sessionStorage.getItem(`tasks_${cacheKey}`);
          if (cachedTask) {
            try {
              return JSON.parse(cachedTask);
            } catch (e) {
              console.error('Error al parsear tarea en caché:', e);
            }
          }
          
          return { 
            success: false, 
            error: error.message,
            data: null 
          };
        } finally {
          // Eliminar la promesa pendiente cuando se complete
          delete apiCache.pendingPromises[`tasks_${cacheKey}`];
        }
      })();
      
      // Almacenar la promesa para posibles solicitudes simultáneas
      apiCache.pendingPromises[`tasks_${cacheKey}`] = taskPromise;
      
      return taskPromise;
    } catch (error) {
      console.error('Error inesperado en getTaskById:', error);
      return { 
        success: false, 
        error: error.message,
        data: null 
      };
    }
  },
  
  /**
   * Actualiza una tarea
   * @param {string} taskId ID de la tarea
   * @param {Object} taskData Datos a actualizar
   * @returns {Promise<Object>} Respuesta con la tarea actualizada
   */
  updateTask: async (taskId, taskData) => {
    try {
      const response = await apiClient.put(`/tasks/${taskId}`, taskData);
      
      // Invalidar cachés relacionadas con tareas
      if (!apiCache.lastFetch.tasks) {
        apiCache.lastFetch.tasks = {};
      }
      
      // Invalidar caché específica de esta tarea
      sessionStorage.removeItem(`tasks_id_${taskId}`);
      delete apiCache.lastFetch.tasks[`id_${taskId}`];
      
      // Invalidar caché de todas las tareas
      sessionStorage.removeItem('tasks_all');
      delete apiCache.lastFetch.tasks['all'];
      
      // Invalidar caché de widget
      sessionStorage.removeItem('tasks_widget');
      delete apiCache.lastFetch.tasks['widget'];
      
      // Invalidar cachés de filtros
      Object.keys(apiCache.lastFetch.tasks).forEach(key => {
        if (key.startsWith('filters_')) {
          delete apiCache.lastFetch.tasks[key];
          sessionStorage.removeItem(`tasks_${key}`);
        }
      });
      
      return response.data;
    } catch (error) {
      console.error('Error al actualizar tarea:', error);
      throw error;
    }
  },
  
  /**
   * Marca una tarea como completada o pendiente
   * @param {string} taskId ID de la tarea
   * @param {boolean} completed Estado de completado
   * @returns {Promise<Object>} Respuesta con la tarea actualizada
   */
  toggleTaskCompletion: async (taskId, completed) => {
    try {
      const response = await apiClient.patch(`/tasks/${taskId}/toggle-completion`, { completed });
      
      // Invalidar cachés relacionadas con tareas
      if (!apiCache.lastFetch.tasks) {
        apiCache.lastFetch.tasks = {};
      }
      
      // Invalidar caché específica de esta tarea
      sessionStorage.removeItem(`tasks_id_${taskId}`);
      delete apiCache.lastFetch.tasks[`id_${taskId}`];
      
      // Invalidar caché de todas las tareas
      sessionStorage.removeItem('tasks_all');
      delete apiCache.lastFetch.tasks['all'];
      
      // Invalidar caché de widget
      sessionStorage.removeItem('tasks_widget');
      delete apiCache.lastFetch.tasks['widget'];
      
      // Invalidar cachés de filtros
      Object.keys(apiCache.lastFetch.tasks).forEach(key => {
        if (key.startsWith('filters_')) {
          delete apiCache.lastFetch.tasks[key];
          sessionStorage.removeItem(`tasks_${key}`);
        }
      });
      
      return response.data;
    } catch (error) {
      console.error('Error al cambiar estado de tarea:', error);
      throw error;
    }
  },
  
  /**
   * Elimina una tarea
   * @param {string} taskId ID de la tarea
   * @returns {Promise<Object>} Respuesta con el resultado
   */
  deleteTask: async (taskId) => {
    try {
      const response = await apiClient.delete(`/tasks/${taskId}`);
      
      // Invalidar cachés relacionadas con tareas
      if (!apiCache.lastFetch.tasks) {
        apiCache.lastFetch.tasks = {};
      }
      
      // Invalidar caché específica de esta tarea
      sessionStorage.removeItem(`tasks_id_${taskId}`);
      delete apiCache.lastFetch.tasks[`id_${taskId}`];
      
      // Invalidar caché de todas las tareas
      sessionStorage.removeItem('tasks_all');
      delete apiCache.lastFetch.tasks['all'];
      
      // Invalidar caché de widget
      sessionStorage.removeItem('tasks_widget');
      delete apiCache.lastFetch.tasks['widget'];
      
      // Invalidar cachés de filtros
      Object.keys(apiCache.lastFetch.tasks).forEach(key => {
        if (key.startsWith('filters_')) {
          delete apiCache.lastFetch.tasks[key];
          sessionStorage.removeItem(`tasks_${key}`);
        }
      });
      
      return response.data;
    } catch (error) {
      console.error('Error al eliminar tarea:', error);
      throw error;
    }
  },
  
  /**
   * Crea una tarea a partir de una nota
   * @param {string} noteId ID de la nota
   * @returns {Promise<Object>} Respuesta con la tarea creada
   */
  createTaskFromNoteById: async (noteId) => {
    try {
      const response = await apiClient.post(`/tasks/from-note/${noteId}`);
      
      // Invalidar caché de tareas
      if (!apiCache.lastFetch.tasks) {
        apiCache.lastFetch.tasks = {};
      }
      
      // Invalidar caché de todas las tareas
      delete apiCache.lastFetch.tasks['all'];
      sessionStorage.removeItem('tasks_all');
      
      // Invalidar caché de widget
      delete apiCache.lastFetch.tasks['widget'];
      sessionStorage.removeItem('tasks_widget');
      
      return response.data;
    } catch (error) {
      console.error('Error al crear tarea desde nota:', error);
      throw error;
    }
  },

  /**
   * Crea un nuevo registro
   * @param {Object} recordData Datos del registro
   * @returns {Promise<Object>} Respuesta con el registro creado
   */
  createRecord: async (recordData) => {
    try {
      const response = await apiClient.post('/records', recordData);
      
      // Invalidar cachés de registros
      if (!apiCache.lastFetch.records) {
        apiCache.lastFetch.records = {};
      }
      
      // Invalidar caché de todos los registros
      delete apiCache.lastFetch.records['all'];
      sessionStorage.removeItem('records_all');
      
      // Si el registro está asociado a un paciente, invalidar su caché
      if (recordData.patientId) {
        delete apiCache.lastFetch.records[`patient_${recordData.patientId}`];
        sessionStorage.removeItem(`records_patient_${recordData.patientId}`);
      }
      
      return response.data;
    } catch (error) {
      console.error('Error al crear registro:', error);
      throw error;
    }
  },
  
  /**
   * Obtiene todos los registros del profesional con control de caché
   * @param {boolean} forceReload Forzar recarga desde el servidor
   * @returns {Promise<Object>} Respuesta con la lista de registros
   */
  getRecords: async (forceReload = false) => {
    try {
      const now = Date.now();
      const cacheKey = 'all';
      
      // Si hay una promesa pendiente para esta solicitud, devolver esa promesa
      if (apiCache.pendingPromises[`records_${cacheKey}`] && !forceReload) {
        return apiCache.pendingPromises[`records_${cacheKey}`];
      }
      
      // Si no se solicita forzar recarga, verificar caché
      if (!forceReload) {
        // Inicializar el objeto si no existe
        if (!apiCache.lastFetch.records) {
          apiCache.lastFetch.records = {};
        }
        
        // Verificar si tenemos caché válida
        if (isCacheValid('records', cacheKey)) {
          const cachedRecords = sessionStorage.getItem(`records_${cacheKey}`);
          if (cachedRecords) {
            try {
              return JSON.parse(cachedRecords);
            } catch (e) {
              console.error('Error al parsear registros en caché:', e);
            }
          }
        }
        
        // Verificar si podemos hacer una nueva solicitud (throttling)
        if (!canMakeRequest('records', cacheKey)) {
          // Si no podemos hacer una solicitud, usar los datos en caché (incluso si están expirados)
          const cachedRecords = sessionStorage.getItem(`records_${cacheKey}`);
          if (cachedRecords) {
            try {
              return JSON.parse(cachedRecords);
            } catch (e) {
              console.error('Error al parsear registros en caché:', e);
            }
          }
        }
      }
      
      // Crear una promesa para la solicitud actual
      const recordsPromise = (async () => {
        try {
          const response = await apiClient.get('/records');
          
          // Actualizar caché
          if (response.data.success) {
            // Guardar en sessionStorage
            sessionStorage.setItem(`records_${cacheKey}`, JSON.stringify(response.data));
            
            // Actualizar tiempo de última obtención
            apiCache.lastFetch.records[cacheKey] = now;
          }
          
          return response.data;
        } catch (error) {
          console.error('Error al obtener registros:', error);
          
          // Intentar usar caché si hay error
          const cachedRecords = sessionStorage.getItem(`records_${cacheKey}`);
          if (cachedRecords) {
            try {
              return JSON.parse(cachedRecords);
            } catch (e) {
              console.error('Error al parsear registros en caché:', e);
            }
          }
          
          return { 
            success: false, 
            error: error.message,
            data: [] 
          };
        } finally {
          // Eliminar la promesa pendiente cuando se complete
          delete apiCache.pendingPromises[`records_${cacheKey}`];
        }
      })();
      
      // Almacenar la promesa para posibles solicitudes simultáneas
      apiCache.pendingPromises[`records_${cacheKey}`] = recordsPromise;
      
      return recordsPromise;
    } catch (error) {
      console.error('Error inesperado en getRecords:', error);
      return { 
        success: false, 
        error: error.message,
        data: [] 
      };
    }
  },
  
  /**
   * Busca registros con filtros y control de caché
   * @param {Object} filters Filtros de búsqueda
   * @param {number} page Número de página
   * @param {number} limit Límite de resultados por página
   * @param {boolean} forceReload Forzar recarga desde el servidor
   * @returns {Promise<Object>} Respuesta con los registros filtrados
   */
  searchRecords: async (filters = {}, page = 1, limit = 20, forceReload = false) => {
    try {
      const queryParams = new URLSearchParams({
        page,
        limit,
        ...filters
      }).toString();
      
      const now = Date.now();
      const cacheKey = `search_${queryParams}`;
      
      // Si hay una promesa pendiente para esta solicitud, devolver esa promesa
      if (apiCache.pendingPromises[`records_${cacheKey}`] && !forceReload) {
        return apiCache.pendingPromises[`records_${cacheKey}`];
      }
      
      // Si no se solicita forzar recarga, verificar caché
      if (!forceReload) {
        // Inicializar el objeto si no existe
        if (!apiCache.lastFetch.records) {
          apiCache.lastFetch.records = {};
        }
        
        // Verificar si tenemos caché válida
        if (isCacheValid('records', cacheKey)) {
          const cachedRecords = sessionStorage.getItem(`records_${cacheKey}`);
          if (cachedRecords) {
            try {
              return JSON.parse(cachedRecords);
            } catch (e) {
              console.error('Error al parsear registros en caché:', e);
            }
          }
        }
        
        // Verificar si podemos hacer una nueva solicitud (throttling)
        if (!canMakeRequest('records', cacheKey)) {
          // Si no podemos hacer una solicitud, usar los datos en caché (incluso si están expirados)
          const cachedRecords = sessionStorage.getItem(`records_${cacheKey}`);
          if (cachedRecords) {
            try {
              return JSON.parse(cachedRecords);
            } catch (e) {
              console.error('Error al parsear registros en caché:', e);
            }
          }
        }
      }
      
      // Crear una promesa para la solicitud actual
      const recordsPromise = (async () => {
        try {
          const response = await apiClient.get(`/records/search?${queryParams}`);
          
          // Actualizar caché
          if (response.data.success) {
            // Guardar en sessionStorage
            sessionStorage.setItem(`records_${cacheKey}`, JSON.stringify(response.data));
            
            // Actualizar tiempo de última obtención
            apiCache.lastFetch.records[cacheKey] = now;
          }
          
          return response.data;
        } catch (error) {
          console.error('Error al buscar registros:', error);
          
          // Intentar usar caché si hay error
          const cachedRecords = sessionStorage.getItem(`records_${cacheKey}`);
          if (cachedRecords) {
            try {
              return JSON.parse(cachedRecords);
            } catch (e) {
              console.error('Error al parsear registros en caché:', e);
            }
          }
          
          return { 
            success: false, 
            error: error.message,
            data: [] 
          };
        } finally {
          // Eliminar la promesa pendiente cuando se complete
          delete apiCache.pendingPromises[`records_${cacheKey}`];
        }
      })();
      
      // Almacenar la promesa para posibles solicitudes simultáneas
      apiCache.pendingPromises[`records_${cacheKey}`] = recordsPromise;
      
      return recordsPromise;
    } catch (error) {
      console.error('Error inesperado en searchRecords:', error);
      return { 
        success: false, 
        error: error.message,
        data: [] 
      };
    }
  },
  
  /**
   * Obtiene un registro por su ID con control de caché
   * @param {string} recordId ID del registro
   * @param {boolean} forceReload Forzar recarga desde el servidor
   * @returns {Promise<Object>} Respuesta con los datos del registro
   */
  getRecordById: async (recordId, forceReload = false) => {
    try {
      const now = Date.now();
      const cacheKey = `id_${recordId}`;
      
      // Si hay una promesa pendiente para esta solicitud, devolver esa promesa
      if (apiCache.pendingPromises[`records_${cacheKey}`] && !forceReload) {
        return apiCache.pendingPromises[`records_${cacheKey}`];
      }
      
      // Si no se solicita forzar recarga, verificar caché
      if (!forceReload) {
        // Inicializar el objeto si no existe
        if (!apiCache.lastFetch.records) {
          apiCache.lastFetch.records = {};
        }
        
        // Verificar si tenemos caché válida
        if (isCacheValid('records', cacheKey)) {
          const cachedRecord = sessionStorage.getItem(`records_${cacheKey}`);
          if (cachedRecord) {
            try {
              return JSON.parse(cachedRecord);
            } catch (e) {
              console.error('Error al parsear registro en caché:', e);
            }
          }
        }
        
        // Verificar si podemos hacer una nueva solicitud (throttling)
        if (!canMakeRequest('records', cacheKey)) {
          // Si no podemos hacer una solicitud, usar los datos en caché (incluso si están expirados)
          const cachedRecord = sessionStorage.getItem(`records_${cacheKey}`);
          if (cachedRecord) {
            try {
              return JSON.parse(cachedRecord);
            } catch (e) {
              console.error('Error al parsear registro en caché:', e);
            }
          }
        }
      }
      
      // Crear una promesa para la solicitud actual
      const recordPromise = (async () => {
        try {
          const response = await apiClient.get(`/records/${recordId}`);
          
          // Actualizar caché
          if (response.data.success) {
            // Guardar en sessionStorage
            sessionStorage.setItem(`records_${cacheKey}`, JSON.stringify(response.data));
            
            // Actualizar tiempo de última obtención
            apiCache.lastFetch.records[cacheKey] = now;
          }
          
          return response.data;
        } catch (error) {
          console.error('Error al obtener registro por ID:', error);
          
          // Intentar usar caché si hay error
          const cachedRecord = sessionStorage.getItem(`records_${cacheKey}`);
          if (cachedRecord) {
            try {
              return JSON.parse(cachedRecord);
            } catch (e) {
              console.error('Error al parsear registro en caché:', e);
            }
          }
          
          return { 
            success: false, 
            error: error.message,
            data: null 
          };
        } finally {
          // Eliminar la promesa pendiente cuando se complete
          delete apiCache.pendingPromises[`records_${cacheKey}`];
        }
      })();
      
      // Almacenar la promesa para posibles solicitudes simultáneas
      apiCache.pendingPromises[`records_${cacheKey}`] = recordPromise;
      
      return recordPromise;
    } catch (error) {
      console.error('Error inesperado en getRecordById:', error);
      return { 
        success: false, 
        error: error.message,
        data: null 
      };
    }
  },
  
  /**
   * Obtiene registros de un paciente con control de caché
   * @param {string} patientId ID del paciente
   * @param {boolean} forceReload Forzar recarga desde el servidor
   * @returns {Promise<Object>} Respuesta con la lista de registros
   */
  getRecordsByPatient: async (patientId, forceReload = false) => {
    try {
      const now = Date.now();
      const cacheKey = `patient_${patientId}`;
      
      // Si hay una promesa pendiente para esta solicitud, devolver esa promesa
      if (apiCache.pendingPromises[`records_${cacheKey}`] && !forceReload) {
        return apiCache.pendingPromises[`records_${cacheKey}`];
      }
      
      // Si no se solicita forzar recarga, verificar caché
      if (!forceReload) {
        // Inicializar el objeto si no existe
        if (!apiCache.lastFetch.records) {
          apiCache.lastFetch.records = {};
        }
        
        // Verificar si tenemos caché válida
        if (isCacheValid('records', cacheKey)) {
          const cachedRecords = sessionStorage.getItem(`records_${cacheKey}`);
          if (cachedRecords) {
            try {
              return JSON.parse(cachedRecords);
            } catch (e) {
              console.error('Error al parsear registros en caché:', e);
            }
          }
        }
        
        // Verificar si podemos hacer una nueva solicitud (throttling)
        if (!canMakeRequest('records', cacheKey)) {
          // Si no podemos hacer una solicitud, usar los datos en caché (incluso si están expirados)
          const cachedRecords = sessionStorage.getItem(`records_${cacheKey}`);
          if (cachedRecords) {
            try {
              return JSON.parse(cachedRecords);
            } catch (e) {
              console.error('Error al parsear registros en caché:', e);
            }
          }
        }
      }
      
      // Crear una promesa para la solicitud actual
      const recordsPromise = (async () => {
        try {
          const response = await apiClient.get(`/records/patient/${patientId}`);
          
          // Actualizar caché
          if (response.data.success) {
            // Guardar en sessionStorage
            sessionStorage.setItem(`records_${cacheKey}`, JSON.stringify(response.data));
            
            // Actualizar tiempo de última obtención
            apiCache.lastFetch.records[cacheKey] = now;
          }
          
          return response.data;
        } catch (error) {
          console.error('Error al obtener registros del paciente:', error);
          
          // Intentar usar caché si hay error
          const cachedRecords = sessionStorage.getItem(`records_${cacheKey}`);
          if (cachedRecords) {
            try {
              return JSON.parse(cachedRecords);
            } catch (e) {
              console.error('Error al parsear registros en caché:', e);
            }
          }
          
          return { 
            success: false, 
            error: error.message,
            data: [] 
          };
        } finally {
          // Eliminar la promesa pendiente cuando se complete
          delete apiCache.pendingPromises[`records_${cacheKey}`];
        }
      })();
      
      // Almacenar la promesa para posibles solicitudes simultáneas
      apiCache.pendingPromises[`records_${cacheKey}`] = recordsPromise;
      
      return recordsPromise;
    } catch (error) {
      console.error('Error inesperado en getRecordsByPatient:', error);
      return { 
        success: false, 
        error: error.message,
        data: [] 
      };
    }
  },
  
  /**
   * Actualiza un registro
   * @param {string} recordId ID del registro
   * @param {Object} recordData Datos a actualizar
   * @returns {Promise<Object>} Respuesta con el registro actualizado
   */
  updateRecord: async (recordId, recordData) => {
    try {
      const response = await apiClient.put(`/records/${recordId}`, recordData);
      
      // Invalidar cachés relacionadas con registros
      if (!apiCache.lastFetch.records) {
        apiCache.lastFetch.records = {};
      }
      
      // Invalidar caché específica de este registro
      sessionStorage.removeItem(`records_id_${recordId}`);
      delete apiCache.lastFetch.records[`id_${recordId}`];
      
      // Invalidar caché de todos los registros
      sessionStorage.removeItem('records_all');
      delete apiCache.lastFetch.records['all'];
      
      // Si el registro está asociado a un paciente, invalidar su caché
      if (recordData.patientId) {
        sessionStorage.removeItem(`records_patient_${recordData.patientId}`);
        delete apiCache.lastFetch.records[`patient_${recordData.patientId}`];
      }
      
      // Invalidar cachés de búsqueda
      Object.keys(apiCache.lastFetch.records).forEach(key => {
        if (key.startsWith('search_')) {
          delete apiCache.lastFetch.records[key];
          sessionStorage.removeItem(`records_${key}`);
        }
      });
      
      return response.data;
    } catch (error) {
      console.error('Error al actualizar registro:', error);
      throw error;
    }
  },
  
  /**
   * Elimina un registro
   * @param {string} recordId ID del registro
   * @returns {Promise<Object>} Respuesta con el resultado
   */
  deleteRecord: async (recordId) => {
    try {
      // Primero obtener los datos del registro para saber qué cachés invalidar
      let patientId;
      try {
        const recordData = sessionStorage.getItem(`records_id_${recordId}`);
        if (recordData) {
          const parsed = JSON.parse(recordData);
          if (parsed.data) {
            patientId = parsed.data.patientId;
          }
        }
      } catch (e) {
        console.error('Error al obtener datos de registro para caché:', e);
      }
      
      const response = await apiClient.delete(`/records/${recordId}`);
      
      // Invalidar cachés relacionadas con registros
      if (!apiCache.lastFetch.records) {
        apiCache.lastFetch.records = {};
      }
      
      // Invalidar caché específica de este registro
      sessionStorage.removeItem(`records_id_${recordId}`);
      delete apiCache.lastFetch.records[`id_${recordId}`];
      
      // Invalidar caché de todos los registros
      sessionStorage.removeItem('records_all');
      delete apiCache.lastFetch.records['all'];
      
      // Si el registro estaba asociado a un paciente, invalidar su caché
      if (patientId) {
        sessionStorage.removeItem(`records_patient_${patientId}`);
        delete apiCache.lastFetch.records[`patient_${patientId}`];
      }
      
      // Invalidar cachés de búsqueda
      Object.keys(apiCache.lastFetch.records).forEach(key => {
        if (key.startsWith('search_')) {
          delete apiCache.lastFetch.records[key];
          sessionStorage.removeItem(`records_${key}`);
        }
      });
      
      return response.data;
    } catch (error) {
      console.error('Error al eliminar registro:', error);
      throw error;
    }
  },
  
  /**
   * Obtiene estadísticas de asistencia con control de caché
   * @param {Object} filters Filtros opcionales (fechas)
   * @returns {Promise<Object>} Respuesta con las estadísticas
   */
  getAttendanceStats: async (filters = {}) => {
    try {
      const queryParams = new URLSearchParams(filters).toString();
      const cacheKey = `stats_${queryParams || 'default'}`;
      
      // Verificar si hay datos en caché
      const cachedStats = sessionStorage.getItem(cacheKey);
      if (cachedStats) {
        try {
          return JSON.parse(cachedStats);
        } catch (e) {
          console.error('Error al parsear estadísticas en caché:', e);
        }
      }
      
      const url = queryParams ? `/records/attendance-stats?${queryParams}` : '/records/attendance-stats';
      const response = await apiClient.get(url);
      
      // Guardar en caché
      if (response.data.success) {
        sessionStorage.setItem(cacheKey, JSON.stringify(response.data));
      }
      
      return response.data;
    } catch (error) {
      console.error('Error al obtener estadísticas de asistencia:', error);
      throw error;
    }
  },

  /**
   * Obtiene registros de un paciente en un rango de fechas con control de caché
   * @param {string} patientId ID del paciente
   * @param {string} startDate Fecha de inicio en formato ISO
   * @param {string} endDate Fecha de fin en formato ISO
   * @param {boolean} forceReload Forzar recarga desde el servidor
   * @returns {Promise<Object>} Respuesta con la lista de registros
   */
  getRecordsByPatientAndDateRange: async (patientId, startDate, endDate, forceReload = false) => {
    try {
      const now = Date.now();
      const cacheKey = `patient_${patientId}_range_${startDate}_${endDate}`;
      
      // Si hay una promesa pendiente para esta solicitud, devolver esa promesa
      if (apiCache.pendingPromises[`records_${cacheKey}`] && !forceReload) {
        return apiCache.pendingPromises[`records_${cacheKey}`];
      }
      
      // Si no se solicita forzar recarga, verificar caché
      if (!forceReload) {
        // Inicializar el objeto si no existe
        if (!apiCache.lastFetch.records) {
          apiCache.lastFetch.records = {};
        }
        
        // Verificar si tenemos caché válida
        if (isCacheValid('records', cacheKey)) {
          const cachedRecords = sessionStorage.getItem(`records_${cacheKey}`);
          if (cachedRecords) {
            try {
              return JSON.parse(cachedRecords);
            } catch (e) {
              console.error('Error al parsear registros en caché:', e);
            }
          }
        }
        
        // Verificar si podemos hacer una nueva solicitud (throttling)
        if (!canMakeRequest('records', cacheKey)) {
          // Si no podemos hacer una solicitud, usar los datos en caché (incluso si están expirados)
          const cachedRecords = sessionStorage.getItem(`records_${cacheKey}`);
          if (cachedRecords) {
            try {
              return JSON.parse(cachedRecords);
            } catch (e) {
              console.error('Error al parsear registros en caché:', e);
            }
          }
        }
      }
      
      // Crear una promesa para la solicitud actual
      const recordsPromise = (async () => {
        try {
          const response = await apiClient.get(`/records/patient/${patientId}/date-range?startDate=${startDate}&endDate=${endDate}`);
          
          // Actualizar caché
          if (response.data.success) {
            // Guardar en sessionStorage
            sessionStorage.setItem(`records_${cacheKey}`, JSON.stringify(response.data));
            
            // Actualizar tiempo de última obtención
            apiCache.lastFetch.records[cacheKey] = now;
          }
          
          return response.data;
        } catch (error) {
          console.error('Error al obtener registros del paciente por rango de fechas:', error);
          
          // Intentar usar caché si hay error
          const cachedRecords = sessionStorage.getItem(`records_${cacheKey}`);
          if (cachedRecords) {
            try {
              return JSON.parse(cachedRecords);
            } catch (e) {
              console.error('Error al parsear registros en caché:', e);
            }
          }
          
          return { 
            success: false, 
            error: error.message,
            data: [] 
          };
        } finally {
          // Eliminar la promesa pendiente cuando se complete
          delete apiCache.pendingPromises[`records_${cacheKey}`];
        }
      })();
      
      // Almacenar la promesa para posibles solicitudes simultáneas
      apiCache.pendingPromises[`records_${cacheKey}`] = recordsPromise;
      
      return recordsPromise;
    } catch (error) {
      console.error('Error inesperado en getRecordsByPatientAndDateRange:', error);
      return { 
        success: false, 
        error: error.message,
        data: [] 
      };
    }
  },

  /**
   * Registra un nuevo profesional independiente
   * @param {Object} userData Datos del profesional
   * @returns {Promise<Object>} Respuesta con el profesional registrado
   */
  registerIndependent: async (userData) => {
    try {
      console.log("Enviando petición de registro de profesional independiente con datos:", userData);
      const response = await apiClient.post('/independent-auth/register', userData);
      console.log("Respuesta de registro:", response.data);
      
      // Extraer y guardar el token de manera más robusta
      let token = null;
      if (response.data?.token) {
        token = response.data.token;
      } else if (response.data?.data?.token) {
        token = response.data.data.token;
      }
      
      if (token) {
        console.log("Token recibido en registro, guardando en localStorage");
        localStorage.setItem('agthealth_token', token);
        
        // Verificar que se guardó correctamente
        const savedToken = localStorage.getItem('agthealth_token');
        if (savedToken !== token) {
          console.error("Error: El token no se guardó correctamente");
        } else {
          console.log("Token guardado correctamente:", savedToken.substring(0, 15) + "...");
        }
      } else {
        console.warn("No se recibió token en la respuesta de registro");
      }
      
      return response.data;
    } catch (error) {
      console.error('Error al registrar profesional independiente:', error);
      throw error;
    }
  },

  /**
   * Inicia sesión con credenciales de profesional independiente
   * @param {Object} credentials Credenciales del profesional
   * @returns {Promise<Object>} Respuesta con el token y datos del profesional
   */
  loginIndependent: async (credentials) => {
    try {
      console.log("Enviando petición de login de profesional independiente con credenciales:", credentials);
      console.log("URL completa:", `${API_URL}/independent-auth/login`);
      
      const response = await apiClient.post('/independent-auth/login', credentials);
      console.log("Respuesta completa de login:", response);
      
      // Extraer y guardar el token de manera más robusta
      let token = null;
      if (response.data?.token) {
        token = response.data.token;
      } else if (response.data?.data?.token) {
        token = response.data.data.token;
      }
      
      if (token) {
        console.log("Token encontrado, guardando en localStorage");
        localStorage.setItem('agthealth_token', token);
        
        // Verificar que se guardó correctamente
        const savedToken = localStorage.getItem('agthealth_token');
        if (savedToken !== token) {
          console.error("Error: El token no se guardó correctamente");
        } else {
          console.log("Token guardado correctamente:", savedToken.substring(0, 15) + "...");
        }
        
        // Limpiar caché al iniciar sesión
        agthealthService.clearCache();
      } else {
        console.warn("No se encontró token en la respuesta");
      }
      
      return response.data;
    } catch (error) {
      console.error('Error detallado al iniciar sesión como profesional independiente:', error);
      console.error('Respuesta del servidor:', error.response?.data);
      console.error('Estado HTTP:', error.response?.status);
      throw error;
    }
  },

  /**
   * Envía un correo para restablecer contraseña de profesional independiente
   * @param {Object} data Datos para el restablecimiento
   * @returns {Promise<Object>} Respuesta con el resultado
   */
  forgotPasswordIndependent: async (data) => {
    try {
      const response = await apiClient.post('/independent-auth/forgot-password', data);
      return response.data;
    } catch (error) {
      console.error('Error al solicitar restablecimiento de contraseña para profesional independiente:', error);
      throw error;
    }
  },

  /**
   * Restablece la contraseña con un token para profesional independiente
   * @param {Object} data Datos para el restablecimiento
   * @returns {Promise<Object>} Respuesta con el resultado
   */
  resetPasswordIndependent: async (data) => {
    try {
      const response = await apiClient.post('/independent-auth/reset-password', data);
      return response.data;
    } catch (error) {
      console.error('Error al restablecer contraseña de profesional independiente:', error);
      throw error;
    }
  },

  /**
   * Verifica si un RUT o SSN ya está registrado como profesional independiente
   * @param {Object} data Datos para la verificación
   * @returns {Promise<Object>} Respuesta con el resultado
   */
  checkIdentificationExistsIndependent: async (data) => {
    try {
      const response = await apiClient.post('/independent-auth/check-id', data);
      return response.data;
    } catch (error) {
      console.error('Error al verificar identificación de profesional independiente:', error);
      throw error;
    }
  },

  /**
   * Crea una sesión de checkout para suscripción
   * @returns {Promise<Object>} URL de la sesión de checkout
   */
  createSubscriptionCheckout: async () => {
    try {
      const response = await apiClient.post('/subscriptions/create-checkout-session');
      return response.data;
    } catch (error) {
      console.error('Error al crear sesión de checkout:', error);
      throw error;
    }
  },
  
  /**
   * Obtiene el estado actual de la suscripción con control de caché
   * @returns {Promise<Object>} Estado de la suscripción
   */
  getSubscriptionStatus: async () => {
    try {
      const cacheKey = 'subscription_status';
      
      // Verificar si hay datos en caché recientes
      const cachedStatus = sessionStorage.getItem(cacheKey);
      if (cachedStatus) {
        const parsed = JSON.parse(cachedStatus);
        const cacheTime = parsed.timestamp || 0;
        
        // Usar caché si tiene menos de 5 minutos
        if (Date.now() - cacheTime < 5 * 60 * 1000) {
          return parsed.data;
        }
      }
      
      const response = await apiClient.get('/subscriptions/status');
      
      // Guardar en caché con timestamp
      if (response.data) {
        sessionStorage.setItem(cacheKey, JSON.stringify({
          data: response.data,
          timestamp: Date.now()
        }));
      }
      
      return response.data;
    } catch (error) {
      console.error('Error al obtener estado de suscripción:', error);
      
      // Intentar usar caché si hay error, incluso si está expirada
      const cachedStatus = sessionStorage.getItem('subscription_status');
      if (cachedStatus) {
        try {
          const parsed = JSON.parse(cachedStatus);
          return parsed.data;
        } catch (e) {
          console.error('Error al parsear estado de suscripción en caché:', e);
        }
      }
      
      throw error;
    }
  },
  
  /**
   * Cancela la suscripción actual
   * @returns {Promise<Object>} Resultado de la cancelación
   */
  cancelSubscription: async () => {
    try {
      const response = await apiClient.post('/subscriptions/cancel');
      
      // Invalidar caché de estado de suscripción
      sessionStorage.removeItem('subscription_status');
      
      return response.data;
    } catch (error) {
      console.error('Error al cancelar suscripción:', error);
      throw error;
    }
  },

  /**
   * Procesa el éxito de una suscripción
   * @param {string} sessionId ID de la sesión de Stripe
   * @returns {Promise<Object>} Resultado del procesamiento
   */
  processSubscriptionSuccess: async (sessionId) => {
    try {
      const response = await apiClient.get(`/subscriptions/success?session_id=${sessionId}`);
      
      // Invalidar caché de estado de suscripción
      sessionStorage.removeItem('subscription_status');
      
      return response.data;
    } catch (error) {
      console.error('Error al procesar éxito de suscripción:', error);
      throw error;
    }
  },

  /**
   * Envía recordatorios por WhatsApp para todas las citas próximas
   * @returns {Promise<Object>} Respuesta con el resultado del envío
   */
  sendAllAppointmentReminders: async () => {
    try {
      const response = await apiClient.post('/reminders/appointments/send-all');
      return response.data;
    } catch (error) {
      console.error('Error al enviar recordatorios de citas:', error);
      throw error;
    }
  },
  
  /**
   * Envía un recordatorio manual para una cita específica por WhatsApp
   * @param {string} appointmentId ID de la cita
   * @returns {Promise<Object>} Respuesta con el resultado
   */
  sendAppointmentReminderWhatsApp: async (appointmentId) => {
    try {
      const response = await apiClient.post(`/reminders/appointments/${appointmentId}/send`);
      return response.data;
    } catch (error) {
      console.error('Error al enviar recordatorio por WhatsApp:', error);
      throw error;
    }
  },
  
  /**
   * Método para limpiar caché (útil para logout o debugging)
   * @returns {Object} Resultado de la operación
   */
  clearCache: () => {
    // Limpiar caché en memoria
    apiCache.userProfile = null;
    apiCache.patients = null;
    
    // Limpiar todos los tiempos de caché
    apiCache.lastFetch = {
      userProfile: null,
      patients: null,
      patientDetails: {},
      appointments: {},
      records: {},
      notes: {},
      tasks: {}
    };
    
    // Limpiar intentos de solicitud
    apiCache.requestAttempts = {};
    
    // Limpiar promesas pendientes
    apiCache.pendingPromises = {};
    
    // Limpiar contadores de errores
    apiCache.errorCount = {};
    
    // Limpiar sessionStorage relacionado con AGT Health
    const keysToRemove = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && (
          key.startsWith('patient_') || 
          key.startsWith('appointments_') || 
          key.startsWith('records_') || 
          key.startsWith('notes_') || 
          key.startsWith('tasks_') ||
          key === 'common_diseases' ||
          key === 'subscription_status'
      )) {
        keysToRemove.push(key);
      }
    }
    
    keysToRemove.forEach(key => sessionStorage.removeItem(key));
    
    return { success: true };
  },
  
  /**
   * Método para obtener el estado actual de la caché (útil para debugging)
   * @returns {Object} Estado de la caché
   */
  getCacheStatus: () => {
    return {
      userProfile: !!apiCache.userProfile,
      patients: !!apiCache.patients,
      lastFetch: { ...apiCache.lastFetch },
      pendingPromises: Object.keys(apiCache.pendingPromises),
      errorCount: { ...apiCache.errorCount }
    };
  },
  
  /**
   * Invalida selectivamente cachés para forzar recarga de datos
   * @param {Array<string>} types Tipos de caché a invalidar ('userProfile', 'patients', 'appointments', etc.)
   * @param {Object} options Opciones adicionales (ej: { patientId: '123' })
   */
  invalidateCache: (types = [], options = {}) => {
    if (types.includes('userProfile')) {
      apiCache.userProfile = null;
      apiCache.lastFetch.userProfile = null;
    }
    
    if (types.includes('patients')) {
      apiCache.patients = null;
      apiCache.lastFetch.patients = null;
      
      // Limpiar cachés de pacientes específicos
      if (options.patientId) {
        sessionStorage.removeItem(`patient_${options.patientId}`);
        if (apiCache.lastFetch.patientDetails) {
          delete apiCache.lastFetch.patientDetails[options.patientId];
        }
      } else {
        // Limpiar todos los detalles de pacientes
        Object.keys(sessionStorage).forEach(key => {
          if (key.startsWith('patient_')) {
            sessionStorage.removeItem(key);
          }
        });
        apiCache.lastFetch.patientDetails = {};
      }
    }
    
    if (types.includes('appointments')) {
      if (!apiCache.lastFetch.appointments) {
        apiCache.lastFetch.appointments = {};
      }
      
      if (options.appointmentId) {
        // Invalidar caché específica de esta cita
        sessionStorage.removeItem(`appointments_id_${options.appointmentId}`);
        delete apiCache.lastFetch.appointments[`id_${options.appointmentId}`];
      } else if (options.patientId) {
        // Invalidar caché de citas por paciente
        sessionStorage.removeItem(`appointments_patient_${options.patientId}`);
        delete apiCache.lastFetch.appointments[`patient_${options.patientId}`];
      } else {
        // Invalidar todas las cachés de citas
        Object.keys(sessionStorage).forEach(key => {
          if (key.startsWith('appointments_')) {
            sessionStorage.removeItem(key);
          }
        });
        apiCache.lastFetch.appointments = {};
      }
    }
    
    if (types.includes('records')) {
      if (!apiCache.lastFetch.records) {
        apiCache.lastFetch.records = {};
      }
      
      if (options.recordId) {
        // Invalidar caché específica de este registro
        sessionStorage.removeItem(`records_id_${options.recordId}`);
        delete apiCache.lastFetch.records[`id_${options.recordId}`];
      } else if (options.patientId) {
        // Invalidar caché de registros por paciente
        sessionStorage.removeItem(`records_patient_${options.patientId}`);
        delete apiCache.lastFetch.records[`patient_${options.patientId}`];
      } else {
        // Invalidar todas las cachés de registros
        Object.keys(sessionStorage).forEach(key => {
          if (key.startsWith('records_')) {
            sessionStorage.removeItem(key);
          }
        });
        apiCache.lastFetch.records = {};
      }
    }
    
    if (types.includes('notes')) {
      if (!apiCache.lastFetch.notes) {
        apiCache.lastFetch.notes = {};
      }
      
      if (options.noteId) {
        // Invalidar caché específica de esta nota
        sessionStorage.removeItem(`notes_id_${options.noteId}`);
        delete apiCache.lastFetch.notes[`id_${options.noteId}`];
      } else {
        // Invalidar todas las cachés de notas
        Object.keys(sessionStorage).forEach(key => {
          if (key.startsWith('notes_')) {
            sessionStorage.removeItem(key);
          }
        });
        apiCache.lastFetch.notes = {};
      }
    }
    
    if (types.includes('tasks')) {
      if (!apiCache.lastFetch.tasks) {
        apiCache.lastFetch.tasks = {};
      }
      
      if (options.taskId) {
        // Invalidar caché específica de esta tarea
        sessionStorage.removeItem(`tasks_id_${options.taskId}`);
        delete apiCache.lastFetch.tasks[`id_${options.taskId}`];
      } else {
        // Invalidar todas las cachés de tareas
        Object.keys(sessionStorage).forEach(key => {
          if (key.startsWith('tasks_')) {
            sessionStorage.removeItem(key);
          }
        });
        apiCache.lastFetch.tasks = {};
      }
    }
    
    return { success: true };
  }
};

// Aplicar throttle a métodos críticos que pueden ser llamados frecuentemente
agthealthService.getTasksForWidget = throttle(agthealthService.getTasksForWidget, 2000);
agthealthService.sendAppointmentReminder = throttle(agthealthService.sendAppointmentReminder, 3000);
agthealthService.sendAppointmentReminderWhatsApp = throttle(agthealthService.sendAppointmentReminderWhatsApp, 3000);

export default agthealthService;